import Vue from 'vue';
import { API_ENDPOINTS, EMAIL_NOTIFICATION, CRON, MESSAGES, NOTIFICATION_SCHEDULE, CHECKLIST_STATUS_ACTION, REJECTION_REMARK, TASK_DETAIL_ERRORS, EVIDENCE_REQUIRED, BULK_UPLOAD_MESSAGES, AUTO_APPROVE, TASKLIST_PROGRESSIVE_LOAD, TASKLIST_LOAD_STATUS, TASKLIST_LOAD_MESSAGES, coerceEvidenceRequired, isEvidenceRequiredEnabled } from '../utils/constants';
import { getApiErrorMessage, parseCycleSchedulerResponse } from '../utils/apiErrorUtils';
import { buildSchedulePayload } from '../utils/notificationScheduleUtils';
import constantsModule from '@/plugins/constants';
import { isValidRejectionRemark, buildChecklistStatusRemarkPayload } from '../utils/tasklistHelpers';
import { parseCycleTaskValidateResult } from '../utils/cycleTaskBulkUpload';
import { withCycleUserRoles, buildCycleUserRolesFromAssignments } from '../views/Setup/TaskList/utils/assignmentRoles';
import { glPayloadFromSelection } from '../utils/taskGlPayload';
import {
  DEPENDENCY_DETAIL_KEYS,
  dependencyNeighborIds,
  taskHasDependencyDetail,
  taskIdString,
} from '../utils/taskDependencyDetail';
import { freezeTaskListReadOnlyNests, taskChecklistForCommit } from '../utils/taskListCommit';
import {
  addPageToBuffer,
  buildRemainingPagePlan,
  checklistPageBody,
  createPageBuffer,
  firstPageBody,
  isCurrentLoad,
  missingPages,
  shouldRefetchTasklist,
  runPagePlan,
} from '../utils/tasklistProgressiveLoad';
import {
  addSchemaCustomColumns,
  canPatchTaskRow,
  editTouchesTaskDependencies,
  mapChecklistRecords,
  BOT_RECON_KEYS,
  mergeGetTaskRow,
  removeTaskRow,
  replaceTaskRow,
} from '../utils/tasklistRows';

// Define baseurl for use in actions, fallback to window.location.origin if not set
const baseurl = process.env.VUE_APP_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// Use Vue.axios which is set up by VueAxios plugin in main.js
function getAxios() {
  return Vue.axios;
}

/** v3 getTask body. Does not write the store. */
async function requestGetTask(checklistId, taskId) {
  const wantedId = taskIdString(taskId);
  const response = await getAxios().post(
    `/v3/financialclose/analysis/gettask/${checklistId}/task/${wantedId}`,
    {},
  );
  const body = response?.data;
  if (body?.status !== 'success' || !body.data) {
    throw new Error(body?.message || TASK_DETAIL_ERRORS.FETCH_FAILED);
  }
  return body.data;
}
// Lazy accessor for $nova to avoid race condition with plugin initialization
function getNova() {
  return Vue.prototype.$nova;
}

function hourToStartTime(time) {
  if (time === null || time === undefined || time === '') return NOTIFICATION_SCHEDULE.DEFAULT_START_TIME;
  const hour = String(time) === '24' ? '00' : String(time).padStart(2, '0');
  return `${hour}:00`;
}

function cycleEscalationFeatureDetails(cycleId, notificationData = {}) {
  const startTime = notificationData.startTime
    || notificationData.schedule?.startTime
    || hourToStartTime(notificationData.time);
  const schedule = { ...buildSchedulePayload({ startTime }), frequencyHours: 24 };
  return {
    feature: EMAIL_NOTIFICATION.FEATURE_TYPES.TASK_OVERDUE,
    flag: notificationData.flag !== false,
    type: EMAIL_NOTIFICATION.TYPE,
    name: notificationData.name,
    subject: notificationData.subject,
    mailBody: notificationData.mailBody,
    cycle_id: cycleId.toString(),
    schedulerTiming: CRON.hourToCronFormat(notificationData.time),
    sla: notificationData.sla,
    priority: notificationData.priority,
    meta: {
      location_key: notificationData.locations,
      toUsers: notificationData.emails,
      start: notificationData.startDate,
      end: notificationData.endDate,
      startDate: notificationData.startDate,
      endDate: notificationData.endDate,
      sla: notificationData.sla,
      category: notificationData.category,
      priority: notificationData.priority,
      schedule,
    },
  };
}

const $constants = Vue.prototype.$constants || constantsModule.default || constantsModule;

// Internal helper to apply reconciliation flag (isReconFound) returned from
// backend responses after file uploads (single or bulk). Accepts one task
// object or an array of task objects. Silently no-ops if flag missing.
function applyReconFlag({ commit, state }, { responseData, tasks, checklistId }) {
  try {
    const flagObj = Array.isArray(responseData?.data) ? responseData.data[0] : null;
    if (!flagObj || !Object.prototype.hasOwnProperty.call(flagObj, 'isReconFound')) return;
    const reconFlag = !!flagObj.isReconFound;
    const taskArray = Array.isArray(tasks) ? tasks : (tasks ? [tasks] : []);
    taskArray.forEach(t => {
      if (!t || !t._id) return;
      if (checklistId) {
        commit('UPDATE_TASK', { checklistId, taskId: t._id, updates: { isReconFound: reconFlag } });
      }
      if (state.currentTask && state.currentTask._id === t._id) {
        commit('SET_CURRENT_TASK', { ...state.currentTask, isReconFound: reconFlag });
      }
    });
  } catch (e) {
    // Swallow intentionally – flag application is best-effort and must not block main flow
  }
}

/** @type {number} Monotonic token; stale GL picker responses are ignored after a newer fetch. */
let tbRequestToken = 0;
/** @type {AbortController|null} Cancels the previous in-flight GL picker request. */
let tbAbortController = null;

function isTbFetchAborted(err) {
  return err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
}

function isStaleTbRequest(requestToken) {
  return requestToken !== tbRequestToken;
}

function invalidateTbInFlightRequests() {
  if (tbAbortController) {
    tbAbortController.abort();
    tbAbortController = null;
  }
  tbRequestToken += 1;
}

function beginTbFetch() {
  invalidateTbInFlightRequests();
  tbAbortController = new AbortController();
  return {
    requestToken: tbRequestToken,
    signal: tbAbortController.signal,
  };
}

/** @type {Promise|null} In-flight getformdata request — prevents parallel duplicate calls. */
let formDataV3Promise = null;
/** @type {{ checklistId: string, promise: Promise, preserveUntilComplete?: boolean, detailsPromise?: Promise }|null} In-flight getAllTasks — prevents parallel duplicate calls. */
let getAllTasksInflight = null;
/** @type {{ key: string, promise: Promise }|null} In-flight per-task v2 getdetails. */
let hydrateDependencyInflight = null;
/** Latest v3 getTask for the open task. Older responses must not commit. */
let openTaskFetchGeneration = 0;
/** @type {AbortController|null} Aborts every getchecklist request of the current tasklist load. */
let tasklistLoadController = null;
/** @type {{ token: object, promise: Promise }|null} Later pages of the current tasklist load. */
let tasklistPagesRun = null;
/** @type {{ token: object, plan: object[], buffer: object }|null} Kept after a failed page for Retry. */
let tasklistRetryState = null;

const CHECKLIST_URL = (checklistId) => `/v3/financialclose/analysis/getchecklist/${checklistId}`;

function isRequestCanceled(err) {
  return err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
}

function abortTasklistRequests() {
  if (tasklistLoadController) {
    tasklistLoadController.abort();
    tasklistLoadController = null;
  }
  tasklistPagesRun = null;
  tasklistRetryState = null;
}

/**
 * New load generation for a cycle. Aborts the previous load's requests; their
 * responses fail the generation check if they still arrive.
 */
function beginTasklistLoad(commit, state, checklistId) {
  abortTasklistRequests();
  const generation = (state.tasklistLoad.generation || 0) + 1;
  commit('SET_TASKLIST_LOAD', {
    cycleId: checklistId,
    generation,
    status: TASKLIST_LOAD_STATUS.LOADING,
    loaded: 0,
    total: 0,
    error: null,
  });
  tasklistLoadController = new AbortController();
  return { cycleId: checklistId, generation, signal: tasklistLoadController.signal };
}

/** One later page. Does not wait on formdata; the first page already resolved it. */
async function fetchChecklistPageRows(checklistId, entry, signal) {
  const response = await getAxios().post(
    CHECKLIST_URL(checklistId),
    checklistPageBody(entry),
    { signal },
  );
  const dt = response?.data;
  if (dt?.status !== 'success' || !dt.data) {
    throw new Error(dt?.message || 'Failed to fetch checklist');
  }
  const records = dt.data.records || dt.data.checklist || [];
  return mapChecklistRecords(records);
}

/**
 * Fetch the remaining pages in the background and append them in page order.
 * A failed page stops scheduling and keeps the loaded rows; Retry resumes the missing pages.
 */
function startRemainingPages({ commit, state }, { token, plan, pages, buffer }) {
  const signal = token.signal;
  const isCurrent = () => !signal.aborted && isCurrentLoad(state.tasklistLoad, token);
  commit('SET_TASKLIST_LOAD', { status: TASKLIST_LOAD_STATUS.LOADING, error: null });

  const promise = runPagePlan({
    pages,
    concurrency: TASKLIST_PROGRESSIVE_LOAD.CONCURRENCY,
    isCurrent,
    fetchPage: (entry) => fetchChecklistPageRows(token.cycleId, entry, signal),
    onPage: (entry, result) => {
      const ready = addPageToBuffer(buffer, entry.index, result.rows);
      if (!ready.length) return;
      commit('APPEND_TASKLIST_ROWS', {
        token,
        rows: freezeTaskListReadOnlyNests(ready),
        customColumns: result.customColumns,
      });
    },
  }).then((outcome) => {
    if (outcome.status === 'stale' || !isCurrent()) return outcome;
    if (outcome.status === 'failed') {
      console.error('[tasks]: tasklist page failed', outcome.failedPage, outcome.error);
      tasklistRetryState = { token, plan, buffer };
      commit('SET_TASKLIST_LOAD', {
        status: TASKLIST_LOAD_STATUS.ERROR,
        error: getApiErrorMessage(outcome.error, TASKLIST_LOAD_MESSAGES.PAGE_FAILED),
      });
      return outcome;
    }
    tasklistRetryState = null;
    // Rows can be added or removed between page requests. Done means every page
    // arrived, so the count shown becomes the rows held.
    const held = (state.taskListData?.checklist || []).length;
    commit('SET_TASKLIST_LOAD', {
      status: TASKLIST_LOAD_STATUS.COMPLETE,
      loaded: held,
      total: held,
      error: null,
    });
    return outcome;
  });

  tasklistPagesRun = { token, promise };
  return promise;
}

function infoManagerRequestsFrom(task) {
  const requests = task && task.__computed && task.__computed.infoManagerRequests;
  return Array.isArray(requests) ? requests : null;
}

/**
 * Cycle fields stored from getdetails when page is tasklist.
 * Matches the tasklist payload. Other cycle attributes are not copied.
 */
const TASKLIST_DETAILS_METADATA_FIELDS = [
  '_id',
  'description',
  'from',
  'to',
  'managerlist',
  'stage',
  'conversation_read',
  'location',
  'pbcproject',
];

/**
 * @param {object|null|undefined} metadata
 * @returns {object}
 */
function tasklistDetailsMetadata(metadata) {
  const source = metadata && typeof metadata === 'object' ? metadata : {};
  const next = {};
  TASKLIST_DETAILS_METADATA_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      next[field] = source[field];
    }
  });
  return next;
}

/**
 * Checklist rows update list fields on the per-task cache. They do not carry
 * getTask link lists. Other tasks drop those lists so a stale getdetails payload
 * is not put back. The open task keeps blocking, blockedBy, relates, canApprove,
 * and __computed.infoManagerRequests from currentTask, then the cache.
 * Bot fields and blocked are copied onto currentTask from this checklist row.
 * @param {object} state
 * @param {object} task incoming checklist row
 * @param {object} merged cache spread under the checklist row; mutated
 */
function applyChecklistRowToTaskCache(state, task, merged) {
  const openId = state.currentTask ? taskIdString(state.currentTask._id) : '';
  const isOpen = openId !== '' && openId === taskIdString(task && task._id);
  if (!isOpen) {
    DEPENDENCY_DETAIL_KEYS.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(task, key)) delete merged[key];
    });
    return;
  }
  const detail = state.currentTask || {};
  const cached = state.tasks[task._id] || {};
  DEPENDENCY_DETAIL_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(detail, key)) {
      merged[key] = detail[key];
    } else if (
      Object.prototype.hasOwnProperty.call(cached, key)
      && !Object.prototype.hasOwnProperty.call(task, key)
    ) {
      merged[key] = cached[key];
    }
  });
  const requests = infoManagerRequestsFrom(detail) || infoManagerRequestsFrom(cached);
  if (requests) {
    const computed = merged.__computed && typeof merged.__computed === 'object'
      ? { ...merged.__computed }
      : {};
    computed.infoManagerRequests = requests;
    merged.__computed = computed;
  }
  copyChecklistOnlyFieldsOntoOpenTask(state, task);
}

/**
 * Checklist can still carry bot recon rows and `blocked` that this getTask body omitted.
 * When that checklist row arrives, copy those onto currentTask
 * and leave the getTask link fields in place.
 */
const CHECKLIST_ONLY_COMPUTED_KEYS = [
  'bot_details',
  'bot_analysis_detail',
  'bot_recon_summary',
  'bot_recon_schedule_amount',
  'bot_recon_explained_difference',
  'bot_recon_unexplained_difference',
];

function copyChecklistOnlyFieldsOntoOpenTask(state, task) {
  if (!state.currentTask || !task) return;
  if (taskIdString(state.currentTask._id) !== taskIdString(task._id)) return;
  const computed = task.__computed && typeof task.__computed === 'object' ? task.__computed : {};
  const next = { ...state.currentTask };
  const nextComputed = { ...(next.__computed || {}) };
  let changed = false;
  if (Object.prototype.hasOwnProperty.call(task, 'blocked') && next.blocked !== task.blocked) {
    next.blocked = task.blocked;
    changed = true;
  }
  CHECKLIST_ONLY_COMPUTED_KEYS.forEach((key) => {
    const fromComputed = Object.prototype.hasOwnProperty.call(computed, key);
    const fromTask = Object.prototype.hasOwnProperty.call(task, key);
    if (!fromComputed && !fromTask) {
      if (BOT_RECON_KEYS.includes(key)) {
        if (Object.prototype.hasOwnProperty.call(next, key)) {
          delete next[key];
          changed = true;
        }
        if (Object.prototype.hasOwnProperty.call(nextComputed, key)) {
          delete nextComputed[key];
          changed = true;
        }
      }
      return;
    }
    const value = fromComputed ? computed[key] : task[key];
    if (next[key] !== value) {
      next[key] = value;
      changed = true;
    }
    if (fromComputed && nextComputed[key] !== computed[key]) {
      nextComputed[key] = computed[key];
      changed = true;
    }
  });
  if (!changed) return;
  next.__computed = nextComputed;
  state.currentTask = next;
}

/**
 * Fetch later getchecklist pages into a local list. The stored checklist is
 * left as it is until the caller commits the finished list.
 * @returns {Promise<{ stale: true } | { stale: false, checklist: object[], customColumns: object }>}
 */
async function gatherChecklistBesideCurrent({ token, state, firstRows, customColumns, plan }) {
  const buffer = createPageBuffer(firstRows);
  const ordered = firstRows.slice();
  const columns = { ...(customColumns || {}) };
  const signal = token.signal;
  const isCurrent = () => !signal.aborted && isCurrentLoad(state.tasklistLoad, token);
  const outcome = await runPagePlan({
    pages: plan,
    concurrency: TASKLIST_PROGRESSIVE_LOAD.CONCURRENCY,
    isCurrent,
    fetchPage: (entry) => fetchChecklistPageRows(token.cycleId, entry, signal),
    onPage: (entry, result) => {
      if (result?.customColumns && Object.keys(result.customColumns).length) {
        Object.assign(columns, result.customColumns);
      }
      const ready = addPageToBuffer(buffer, entry.index, result?.rows);
      if (ready.length) ordered.push(...ready);
    },
  });
  if (outcome.status === 'stale' || !isCurrent()) return { stale: true };
  if (outcome.status === 'failed') {
    console.error('[tasks]: tasklist page failed', outcome.failedPage, outcome.error);
    throw outcome.error || new Error(TASKLIST_LOAD_MESSAGES.PAGE_FAILED);
  }
  return {
    stale: false,
    checklist: freezeTaskListReadOnlyNests(ordered),
    customColumns: columns,
  };
}

/** Cycle-page caches. They stay on taskListData and formDataV3, not on the open task. */
const CYCLE_LIST_CACHE_KEYS = ['checklist', 'locationList', 'locationThresholds', 'metadata', 'summary', 'usersList'];

/** Client copies of workflow/workpaper fields. Submit/approve and file APIs own these. */
const CYCLE_TASK_CLIENT_OWNED_WRITE_KEYS = [
  'upload',
  'upload_v2',
  'stage',
  'approval_state',
  'status',
  'workflow_phase',
];

function omitKeys(row, keys) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
  const next = { ...row };
  keys.forEach((key) => delete next[key]);
  return next;
}

function omitCycleListCaches(row) {
  return omitKeys(row, CYCLE_LIST_CACHE_KEYS);
}

function omitCycleListCachesFromPayload(data) {
  if (Array.isArray(data)) return data.map((row) => omitCycleListCaches(row));
  return omitCycleListCaches(data);
}

function omitCycleTaskClientOwnedWriteFields(row) {
  return omitKeys(row, CYCLE_TASK_CLIENT_OWNED_WRITE_KEYS);
}

function omitCycleTaskClientOwnedWriteFieldsFromPayload(data) {
  if (Array.isArray(data)) return data.map((row) => omitCycleTaskClientOwnedWriteFields(row));
  return omitCycleTaskClientOwnedWriteFields(data);
}

const TASK_LINK_KEYS = ['blocking', 'blockedBy', 'relates'];

/**
 * Open-task record from v3 getTask.
 * Link fields are taken from the getTask body when the server sends them.
 * mergeGetTaskRow keeps the previous bot fields when the fresh __computed omits them.
 * @param {object} existing previous open task (currentTask / state.tasks)
 * @param {object} fresh getTask data
 * @returns {object}
 */
function taskDetailFromGetTask(existing, fresh) {
  const source = fresh || {};
  const task = omitCycleListCaches(mergeGetTaskRow(existing || {}, source));
  TASK_LINK_KEYS.forEach((key) => {
    if (Array.isArray(source[key])) {
      task[key] = source[key];
    } else if (Array.isArray(existing?.[key])) {
      task[key] = existing[key];
    }
  });
  if (typeof source.canApprove === 'boolean') {
    task.canApprove = source.canApprove;
  } else if (typeof existing?.canApprove === 'boolean') {
    task.canApprove = existing.canApprove;
  } else if (typeof task.canApprove !== 'boolean') {
    task.canApprove = task.blocked !== true;
  }
  return task;
}

export default {
  namespaced: true,
  
  state: {
    tasks: {},  // Keyed by taskId for quick access
    currentTask: null,
    loading: false,
    error: null,
    taskLists: {}, // Keyed by projectId/checklistId
    // Initialize empty task list data with all expected properties
    taskListData: {
      metadata: null,
      checklist: [],
      pbcitems: [],
      summary: {
        manager: {},
        preparer: {},
        approver: {}
      },
      locationList: [],
      usersList: [],
      locationThresholds: [],
      infomangerrequests: [],
      chartAcc: [],
      coaCategoryAcc: [],
      coaSubcategoryAcc: [],
      categoryAcc: [],
      subcategoryAcc: [],
      customColumns: {}
    },
    managersummary: {},
    preparersummary: {},
    approversummary: {},
    locationList: [],
    enabledLocations: [],
    usersList: [],
    locationThresholds: [],
    infomangerrequests: [],
    chartAcc: [],
    tasklistLoaded: false,
    // Progressive tasklist load (F4). generation is bumped on every new load and on leave.
    tasklistLoad: {
      cycleId: null,
      generation: 0,
      status: TASKLIST_LOAD_STATUS.IDLE,
      loaded: 0,
      total: 0,
      error: null,
    },
    notificationEnabled: false,
    // Custom layouts keyed by page name (e.g. financialclose_master_checklist)
    customLayouts: {},
    statusDetails: null,
    transactionMatchingList: null,
    // For email notifications
    emailNotifications: [],
    emailNotificationsLoading: false,
    isCycleInActive: false,
    // UI flags for bulk upload
    drawerVisible: false,
    bulkworkpaperupload: false,
    bulkfilesloading: 0,
    sendForReviewDialogbulk: false,
    sendforreviewdrawer: false,
    userDetails:{},
    activeUsers:[],
    inactiveUsers:[],
    masterCategories: {
      categoryAcc: [],
      subcategoryAcc: [],
    },
    filteredLocations: [],
    filteredLocationsCycleId: null,
    // Horizon UI state keyed by projectId (close cycle; survives back navigation)
    horizonViewState: {},
    // List view UI state keyed by projectId (close cycle; role, pipeline tab, pagination; filters on back-nav only)
    listViewState: {},
    // Last List/Horizon view per checklist (restored on task-detail back navigation)
    tasklistViewByChecklist: {},
    // v3 formdata cache (client-level reference data)
    formDataV3: null,
    // TB pagination state (for GL picker infinite scroll - Phase 2)
    tbPagination: { page: 1, limit: 50, totalCount: 0, totalPages: 0 },
    tbRecords: [],
    tbLoading: false,
    tbFullyLoaded: false,
  },
  
  mutations: {
    RESET_BULK_UPLOAD_UI(state) {
      state.drawerVisible = false;
      state.bulkworkpaperupload = false;
      state.bulkfilesloading = 1;
      state.sendForReviewDialogbulk = false;
      state.sendforreviewdrawer = false;
    },
    SET_DRAWER_VISIBLE(state, value) {
      state.drawerVisible = value;
    },
    SET_SEND_FOR_REVIEW_DRAWER(state, value) {
      state.sendforreviewdrawer = value;
    },
    CLEAR_TASKLIST(state) {
      // Reset to initial state with empty arrays instead of undefined values
      state.taskListData = {
        metadata: null,
        checklist: [],
        tb: [],
        pbcitems: [],
        summary: {
          manager: {},
          preparer: {},
          approver: {}
        },
        locationList: [],
        usersList: [],
        locationThresholds: [],
        infomangerrequests: [],
        chartAcc: [],
        coaCategoryAcc: [],
        coaSubcategoryAcc: [],
        categoryAcc: [],
        subcategoryAcc: [],
        customColumns: {}
      };
      // Reset filtered locations so they are recomputed for the next cycle
      state.filteredLocations = [];
      state.filteredLocationsCycleId = null;
      // Phase 2: Also reset TB pagination state
      invalidateTbInFlightRequests();
      state.tbRecords = [];
      state.tbPagination = { page: 1, limit: 50, totalCount: 0, totalPages: 0 };
      state.tbLoading = false;
      state.tbFullyLoaded = false;
    },
    SET_TASKLIST_LOADED(state, value) {
      state.tasklistLoaded = value;
    },
    SET_TASKLIST_LOAD(state, partial) {
      state.tasklistLoad = { ...state.tasklistLoad, ...partial };
    },
    /**
     * Append a later page of the current load. Pages from an older generation or
     * another cycle are dropped. The array is replaced so list watchers re-run.
     */
    APPEND_TASKLIST_ROWS(state, { token, rows, customColumns }) {
      if (!isCurrentLoad(state.tasklistLoad, token)) return;
      const data = state.taskListData;
      if (!data || data.metadata?._id !== token.cycleId) return;
      const nextChecklist = (Array.isArray(data.checklist) ? data.checklist : []).concat(rows || []);
      data.checklist = nextChecklist;
      if (customColumns && Object.keys(customColumns).length) {
        data.customColumns = { ...(data.customColumns || {}), ...customColumns };
      }
      (rows || []).forEach((task) => {
        if (!task || !task._id) return;
        const merged = { ...(state.tasks[task._id] || {}), ...task };
        // List rows stay checklist-shaped. The open task's cache keeps getTask
        // link fields; other tasks drop link lists this page does not carry.
        applyChecklistRowToTaskCache(state, task, merged);
        Vue.set(state.tasks, task._id, merged);
      });
      state.tasklistLoad = { ...state.tasklistLoad, loaded: nextChecklist.length };
    },
    /** Replace one list row in place (single-task refresh). Order is unchanged. */
    PATCH_TASKLIST_ROW(state, { checklistId, row }) {
      const data = state.taskListData;
      if (!row?._id || !data || data.metadata?._id !== checklistId) return;
      const { list, index } = replaceTaskRow(data.checklist, row);
      if (index === -1) return;
      data.checklist = list;
      Vue.set(state.tasks, row._id, { ...(state.tasks[row._id] || {}), ...row });
      const cachedList = state.taskLists[checklistId];
      if (Array.isArray(cachedList)) {
        Vue.set(state.taskLists, checklistId, replaceTaskRow(cachedList, row).list);
      }
      if (state.currentTask && taskIdString(state.currentTask._id) === taskIdString(row._id)) {
        state.currentTask = { ...state.currentTask, ...row };
      }
    },
    REMOVE_TASKLIST_ROW(state, { checklistId, taskId }) {
      const data = state.taskListData;
      if (!data || data.metadata?._id !== checklistId) return;
      const before = Array.isArray(data.checklist) ? data.checklist.length : 0;
      data.checklist = removeTaskRow(data.checklist, taskId);
      if (data.checklist.length !== before && state.tasklistLoad.cycleId === checklistId) {
        const loaded = Math.max(0, state.tasklistLoad.loaded - 1);
        const total = Math.max(0, state.tasklistLoad.total - 1);
        state.tasklistLoad = { ...state.tasklistLoad, loaded, total };
      }
    },
    /** getdetails part of the tasklist (metadata, role summaries). Rows are untouched. */
    SET_TASKLIST_DETAILS(state, { checklistId, metadata, summary }) {
      const data = state.taskListData;
      if (!data || !checklistId) return;
      // A cold task-detail load has metadata: null. Still store this cycle's getdetails.
      // Skip only when the store already holds a different cycle.
      const currentId = data.metadata && data.metadata._id;
      if (currentId != null && currentId !== checklistId) return;
      if (metadata && metadata._id != null && metadata._id !== checklistId) return;
      if (metadata) data.metadata = metadata;
      if (summary) {
        data.summary = summary;
        state.managersummary = summary.manager || {};
        state.preparersummary = summary.preparer || {};
        state.approversummary = summary.approver || {};
      }
    },
    SET_EMAIL_NOTIFICATIONS(state, notifications) {
      state.emailNotifications = notifications || [];
    },
    SET_EMAIL_NOTIFICATIONS_LOADING(state, isLoading) {
      state.emailNotificationsLoading = isLoading;
    },
    SET_CYCLE_STATUS(state, isInactive) {
      state.isCycleInActive = isInactive;
    },
    SET_MANAGER_SUMMARY(state, payload) {
      state.managersummary = payload;
    },
    SET_PREPARER_SUMMARY(state, payload) {
      state.preparersummary = payload;
    },
    SET_APPROVER_SUMMARY(state, payload) {
      state.approversummary = payload;
    },
    SET_LOCATION_LIST(state, payload) {
      state.locationList = payload;
    },
    SET_ENABLED_LOCATIONS(state, payload) {
      state.enabledLocations = payload;
    },
    SET_USERS_LIST(state, payload) {
      state.usersList = payload;
    },
    SET_LOCATION_THRESHOLDS(state, payload) {
      state.locationThresholds = payload;
    },
    SET_INFOMANAGER_REQUESTS(state, payload) {
      state.infomangerrequests = payload;
    },
    SET_CHART_ACC(state, payload) {
      state.chartAcc = payload;
    },
    SET_CUSTOM_LAYOUT(state, payload) {
      if (payload === null) {
        state.customLayouts = {};
        return;
      }
      const page = payload?.page;
      if (!page) return;
      Vue.set(state.customLayouts, page, payload);
    },
    SET_CUSTOM_LAYOUT_PAGE(state, { page, data }) {
      if (!page) return;
      Vue.set(state.customLayouts, page, data);
    },
    MERGE_CUSTOM_LAYOUT_PAGE(state, { page, custom_layout, scopedKey, filter }) {
      if (!page || !scopedKey) return;
      const existing = state.customLayouts[page] || {};
      const existingFilters =
        existing.filter && typeof existing.filter === 'object' ? existing.filter : {};
      Vue.set(state.customLayouts, page, {
        ...existing,
        custom_layout,
        filter: { ...existingFilters, [scopedKey]: filter || {} },
        page,
      });
    },
    CLEAR_CUSTOM_LAYOUT_PAGE(state, page) {
      if (!page) return;
      Vue.delete(state.customLayouts, page);
    },
    SET_STATUS_DETAILS(state, payload) {
      state.statusDetails = payload;
    },
    SET_TRANSACTION_MATCHING_LIST(state, payload) {
      state.transactionMatchingList = payload;
    },
    // Set the current task
    SET_CURRENT_TASK(state, task) {
      if (!task) {
        state.currentTask = task;
        return;
      }

      // Keep a single source of truth for the task object across:
      // - state.currentTask (task details page)
      // - state.tasks[taskId] (task cache used by getTaskById)
      //
      // This prevents stale cached tasks from overwriting the latest currentTask.
      if (task && task._id) {
        const previousCached = state.tasks[task._id] || {};
        const merged = { ...previousCached, ...task };
        Vue.set(state.tasks, task._id, merged);
        state.currentTask = merged;
      } else {
        state.currentTask = task;
      }
    },

    SET_ALL_USERS_LIST(state, users) {
      state.userDetails = users || [];
    },
    SET_ACTIVE_USERS(state, users) {
      state.activeUsers = users || [];
    },
    SET_INACTIVE_USERS(state, users) {
      state.inactiveUsers = users || [];
    },
    SET_MASTER_CATEGORIES(state, { categoryAcc, subcategoryAcc }) {
      state.masterCategories = {
        categoryAcc: categoryAcc || [],
        subcategoryAcc: subcategoryAcc || [],
      };
    },
    SET_FILTERED_LOCATIONS(state, { locations, cycleId } = {}) {
      state.filteredLocations = locations || [];
      if (cycleId !== undefined) state.filteredLocationsCycleId = cycleId;
    },
    SET_HORIZON_VIEW_STATE(state, { projectId, partial }) {
      if (!projectId) return;
      state.horizonViewState = {
        ...state.horizonViewState,
        [projectId]: {
          ...(state.horizonViewState[projectId] || {}),
          ...partial,
        },
      };
    },
    CLEAR_HORIZON_VIEW_STATE(state, projectId) {
      if (!projectId) return;
      const next = { ...state.horizonViewState };
      delete next[projectId];
      state.horizonViewState = next;
    },
    SET_LIST_VIEW_STATE(state, { projectId, partial }) {
      if (!projectId) return;
      state.listViewState = {
        ...state.listViewState,
        [projectId]: {
          ...(state.listViewState[projectId] || {}),
          ...partial,
        },
      };
    },
    CLEAR_LIST_VIEW_STATE(state, projectId) {
      if (!projectId) return;
      const next = { ...state.listViewState };
      delete next[projectId];
      state.listViewState = next;
    },
    SET_TASKLIST_VIEW(state, { checklistId, view }) {
      if (!checklistId || !view) return;
      state.tasklistViewByChecklist = {
        ...state.tasklistViewByChecklist,
        [checklistId]: view,
      };
    },
    SET_FORM_DATA_V3(state, data) {
      state.formDataV3 = data;
    },
    // TB pagination mutations (Phase 2)
    SET_TB_RECORDS(state, { records, append }) {
      if (append) {
        state.tbRecords = [...state.tbRecords, ...records];
      } else {
        state.tbRecords = records;
      }
    },
    SET_TB_PAGINATION(state, pagination) {
      state.tbPagination = { ...state.tbPagination, ...pagination };
    },
    SET_TB_LOADING(state, loading) {
      state.tbLoading = loading;
    },
    SET_TB_FULLY_LOADED(state, fullyLoaded) {
      state.tbFullyLoaded = fullyLoaded;
    },
    RESET_TB_STATE(state) {
      invalidateTbInFlightRequests();
      state.tbRecords = [];
      state.tbPagination = { page: 1, limit: 50, totalCount: 0, totalPages: 0 };
      state.tbLoading = false;
      state.tbFullyLoaded = false;
    },
    
    // Store task in cache
    CACHE_TASK(state, task) {
      if (task && task._id) {
        Vue.set(state.tasks, taskIdString(task._id) || task._id, task);
      }
    },
    
    // Set the loading state
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    
    // Set error state
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    // Update or store a task list
    SET_TASK_LIST(state, { checklistId, tasks }) {
      Vue.set(state.taskLists, checklistId, tasks);
      
      // Also cache individual tasks for quick access
      if (Array.isArray(tasks)) {
        tasks.forEach(task => {
          if (task && task._id) {
            Vue.set(state.tasks, task._id, task);
          }
        });
      }
    },
    
    // Update a single task in both tasks and taskLists
    UPDATE_TASK(state, { checklistId, taskId, updates }) {
      const assignmentFields = ['preparer', 'approver', 'sign_off_approvers', 'requires_sign_off'];
      const touchesAssignment = assignmentFields.some((field) => Object.prototype.hasOwnProperty.call(updates, field));
      const patchedUpdates = touchesAssignment
        ? { ...updates, _assignmentPatchedAt: Date.now() }
        : updates;

      // Update in tasks cache
      if (state.tasks[taskId]) {
        Vue.set(state.tasks, taskId, { ...state.tasks[taskId], ...patchedUpdates });
      } else if (patchedUpdates && (patchedUpdates._id || taskId)) {
        // Best-effort populate cache if it didn't exist yet
        Vue.set(state.tasks, taskId, { ...patchedUpdates, _id: patchedUpdates._id || taskId });
      }

      // Keep taskListData.checklist aligned so list + detail views stay in sync
      // after inline edits without requiring a full page refresh.
      const checklist = state.taskListData?.checklist;
      const metadataId = state.taskListData?.metadata?._id;
      if (Array.isArray(checklist) && (!checklistId || metadataId === checklistId)) {
        const index = checklist.findIndex((task) => task && task._id === taskId);
        if (index !== -1) {
          Vue.set(checklist, index, {
            ...checklist[index],
            ...patchedUpdates,
          });
        }
      }
      
      // Update in taskLists if it exists
      if (state.taskLists[checklistId]) {
        const index = state.taskLists[checklistId].findIndex(task => task._id === taskId);
        if (index !== -1) {
          Vue.set(state.taskLists[checklistId], index, {
            ...state.taskLists[checklistId][index],
            ...patchedUpdates
          });
        }
      }
      
      // Update currentTask if it's the same task
      if (state.currentTask && taskIdString(state.currentTask._id) === taskIdString(taskId)) {
        state.currentTask = { ...state.currentTask, ...patchedUpdates };
      }
    },
    
    /**
     * Set the entire tasklist data
     * This is used for maintaining tasklist data across views
     */
    SET_TASKLIST_DATA(state, data) {
      // Store the entire tasklist data
      state.taskListData = data;

      if (data?.summary) {
        state.managersummary = data.summary.manager || {};
        state.preparersummary = data.summary.preparer || {};
        state.approversummary = data.summary.approver || {};
      }

      // Keep the per-task cache aligned with the latest checklist payload.
      // Preserve recently patched assignment fields when getchecklist lags behind editchecklist.
      const checklist = data && Array.isArray(data.checklist) ? data.checklist : [];
      const assignmentFields = ['preparer', 'approver', 'sign_off_approvers', 'requires_sign_off'];
      const assignmentPatchTtlMs = 60 * 1000;
      checklist.forEach(task => {
        if (task && task._id) {
          const previousCached = state.tasks[task._id] || {};
          const merged = { ...previousCached, ...task };
          // Checklist rows update list fields only. The open task keeps getTask
          // link fields on the cache. This does not replace currentTask with the
          // checklist row; getTaskById prefers that record.
          applyChecklistRowToTaskCache(state, task, merged);
          const patchAge = previousCached._assignmentPatchedAt
            ? Date.now() - previousCached._assignmentPatchedAt
            : null;
          if (patchAge !== null && patchAge < assignmentPatchTtlMs) {
            assignmentFields.forEach((field) => {
              if (previousCached[field] !== undefined) {
                merged[field] = previousCached[field];
              }
            });
            merged._assignmentPatchedAt = previousCached._assignmentPatchedAt;
          }
          Vue.set(state.tasks, task._id, merged);

          const checklistIndex = checklist.findIndex((row) => row && row._id === task._id);
          if (checklistIndex !== -1 && patchAge !== null && patchAge < assignmentPatchTtlMs) {
            assignmentFields.forEach((field) => {
              if (previousCached[field] !== undefined) {
                checklist[checklistIndex][field] = previousCached[field];
              }
            });
          }
        }
      });
    },
    SET_NOTIFICATION_ENABLED(state, value) {
      state.notificationEnabled = value;
    },

    /**
     * Drop stored getdetails link lists so the next open of these tasks loads them again.
     * Keys are deleted. An empty array still counts as already loaded.
     */
    CLEAR_TASK_DEPENDENCY_DETAIL(state, { taskIds } = {}) {
      const ids = [...new Set((taskIds || []).map(taskIdString).filter(Boolean))];
      const strip = (row) => {
        if (!row) return;
        DEPENDENCY_DETAIL_KEYS.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(row, key)) {
            Vue.delete(row, key);
          }
        });
      };
      ids.forEach((taskId) => {
        strip(state.tasks[taskId]);
        if (state.currentTask && taskIdString(state.currentTask._id) === taskId) {
          strip(state.currentTask);
        }
        const checklist = state.taskListData?.checklist;
        if (Array.isArray(checklist)) {
          strip(checklist.find((task) => task && taskIdString(task._id) === taskId));
        }
        Object.keys(state.taskLists || {}).forEach((checklistId) => {
          const list = state.taskLists[checklistId];
          if (!Array.isArray(list)) return;
          strip(list.find((task) => task && taskIdString(task._id) === taskId));
        });
      });
    },
  },
  
  actions: {
    /**
     * Add or edit a checklist item (used by Tasklist.vue, TaskEditDetails.vue, and TaskGLSelection.vue)
     * @param {Object} payload { addeditdata, customColumns, selectglsLocal, projectId }
     * projectId is required for "add" mode (when addeditdata._id is absent)
     * @returns {Object} result { success, data, errorMessage }
     */
    async addEditChecklistItem({ commit, state }, { addeditdata, customColumns, selectglsLocal, projectId }) {
      try {
        // Fallback if $constants not injected in this scope
        const localConstants = $constants;
        // Clean undefined fields
        Object.keys(addeditdata).forEach(key => {
          if (addeditdata[key] === undefined) delete addeditdata[key];
        });

        // Prepare customfields
        let customfields = [];
        if (Array.isArray(customColumns) && customColumns.length > 0) {
          customfields = customColumns.map(column => ({
            [column.columnName]:
              addeditdata[column.columnName] !== undefined
                ? addeditdata[column.columnName]
                : column.dataType === 'number' ? 0 : ''
          }));
        }
        addeditdata.customfields = customfields;
        // Remove individual custom column keys from top-level (they are now in customfields)
        if (Array.isArray(customColumns) && customColumns.length > 0) {
          customColumns.forEach(column => {
            delete addeditdata[column.columnName];
          });
        }
        // Validate no_of_evidence
        if (typeof addeditdata.no_of_evidence !== 'number' || addeditdata.no_of_evidence < 0 || addeditdata.no_of_evidence > 10) {
          return { success: false, errorMessage: EVIDENCE_REQUIRED.COUNT_RANGE_ERROR, data: { index0: { no_of_evidence: [EVIDENCE_REQUIRED.COUNT_RANGE_ERROR] } } };
        }
        if (
          isEvidenceRequiredEnabled(addeditdata.evidence_required)
          && addeditdata.no_of_evidence < 1
        ) {
          return {
            success: false,
            errorMessage: EVIDENCE_REQUIRED.COUNT_REQUIRED_ERROR,
            data: { index0: { no_of_evidence: [EVIDENCE_REQUIRED.COUNT_REQUIRED_ERROR] } },
          };
        }
        addeditdata.evidence_required = coerceEvidenceRequired(addeditdata.evidence_required);
        // Validate task_type
        const TASK_TYPES = (localConstants || {}).TASK_LIST_CONSTANTS?.TASK_TYPES || {};
        if (!Object.values(TASK_TYPES).includes(addeditdata.task_type)) {
          return { success: false, errorMessage: 'Task Type is required. Please select a value.', data: { index0: { task_type: ['Task Type is required. Please select a value.'] } } };
        }
        // Validate priority
        if (!addeditdata.priority) {
          return { success: false, errorMessage: 'Priority is required. Please select a value.', data: { index0: { priority: ['Priority is required. Please select a value.'] } } };
        }
        // Validate GL selection
        if (addeditdata.task_type === TASK_TYPES.NORMAL) {
          if (selectglsLocal && selectglsLocal.length) {
            return { success: false, errorMessage: 'GL selection is not allowed for Checklist task type.', data: { index0: { glcode: ['Not Allowed'] } } };
          }
        } else if (addeditdata.task_type === TASK_TYPES.ACCOUNT_RECONCILIATION) {
          if (!selectglsLocal || !selectglsLocal.length) {
            const ERR_MSG = (localConstants || {}).TASK_LIST_CONSTANTS?.ERROR_MESSAGES?.GL_CODE_REQUIRED || 'At least one GL required.';
            return { success: false, errorMessage: 'Account selection is required for Account Reconciliation.', data: { index0: { glcode: ERR_MSG } } };
          }
        }
        // Prepare payload for backend.
        // glcode is account-code strings; __tblinks stays the trial-balance ids.
        let ndt = withCycleUserRoles({ ...addeditdata });
        const glPayload = glPayloadFromSelection(selectglsLocal, ndt);
        ndt.glcode = glPayload.glcode;
        ndt.__tblinks = glPayload.__tblinks;
        const isEdit = !!addeditdata._id;
        delete ndt._id;
        delete ndt.customColumns;
        if (isEdit) {
          ndt = omitCycleTaskClientOwnedWriteFields(ndt);
          // Cycle-level list caches can ride on the task object. The edit API does not use them.
          ndt = omitCycleListCaches(ndt);
        }
        const url = isEdit
          ? `/v2/financialclose/analysis/editchecklist/${addeditdata._id}`
          : `/v2/financialclose/analysis/addchecklist/${projectId}`;
        // editchecklist reads `data` as an object (same as editChecklistAssignment).
        // addchecklist reads `data[0]`.
        const requestBody = isEdit
          ? { data: { ...ndt, project: projectId } }
          : { data: [{ ...ndt, project: projectId }] };
        const response = await getAxios().post(url, requestBody);
        if (response.data.status === 'success') {
          const createdTask = !isEdit && Array.isArray(response.data.data)
            ? response.data.data[0]
            : null;
          if (createdTask?._id) {
            commit('CACHE_TASK', {
              ...ndt,
              ...createdTask,
              project: projectId || createdTask.project,
            });
          }
          if (isEdit && addeditdata._id && projectId) {
            commit('UPDATE_TASK', {
              checklistId: projectId,
              taskId: addeditdata._id,
              updates: {
                preparer: addeditdata.preparer,
                approver: addeditdata.approver,
                sign_off_approvers: addeditdata.sign_off_approvers,
                requires_sign_off: addeditdata.requires_sign_off,
                user_roles: ndt.user_roles,
              },
            });
          }
          // No links before and none sent: the stored (empty) link lists are still right.
          const linkEdit = editTouchesTaskDependencies(addeditdata)
            || (addeditdata._id && dependencyNeighborIds(state.tasks[addeditdata._id]).length > 0);
          if (linkEdit) {
            const taskIds = new Set();
            const editedId = taskIdString(addeditdata._id || createdTask?._id);
            if (editedId) taskIds.add(editedId);
            (addeditdata.links || []).forEach((link) => {
              const id = taskIdString(link?.taskId);
              if (id) taskIds.add(id);
            });
            (addeditdata.unlinkWaitingOn || []).forEach((id) => {
              const neighborId = taskIdString(id);
              if (neighborId) taskIds.add(neighborId);
            });
            if (addeditdata._id) {
              dependencyNeighborIds(state.tasks[addeditdata._id]).forEach((id) => taskIds.add(id));
            }
            console.log('[tasks]: clear dependency detail', [...taskIds]);
            commit('CLEAR_TASK_DEPENDENCY_DETAIL', { taskIds: [...taskIds] });
          }
          return { success: true, data: ndt, isEdit, createdTask };
        } else {
          const fieldErrors = response.data?.data?.fieldErrors;
          if (fieldErrors && typeof fieldErrors === 'object' && Object.keys(fieldErrors).length) {
            const fieldErrorLists = {};
            Object.entries(fieldErrors).forEach(([key, message]) => {
              fieldErrorLists[key] = Array.isArray(message) ? message : [message];
            });
            return { success: false, errorMessage: response.data.message, data: { index0: fieldErrorLists } };
          }
          if (response.data.message) {
            return { success: false, errorMessage: response.data.message };
          } else if (response.data.data && response.data.data.length > 0 && response.data.data[0].index0) {
            return { success: false, data: response.data.data[0] };
          } else {
            throw new Error(response.data.message || 'Unknown error!');
          }
        }
      } catch (err) {
        return { success: false, errorMessage: err.message || 'Unknown error!' };
      }
    },
    /**
     * Delete one checklist task and update the cycle tasklist in the store.
     * A task with no dependency links is removed from the list and only the
     * summaries are re-read when the progressive load is already complete.
     * Otherwise the list is reloaded, because a delete shifts page offsets and
     * deleting a blocker changes `blocked` on other rows.
     * A failed reload does not undo a successful delete.
     * @param {Object} payload { checklistId, taskId }
     * @returns {Object} { success, errorMessage?, refreshError? }
     */
    async deleteChecklistItem({ commit, dispatch, state }, { checklistId, taskId }) {
      const deletedTask = state.tasks[taskId]
        || (state.currentTask && state.currentTask._id === taskId ? state.currentTask : null);
      const rowOnly = canPatchTaskRow(deletedTask)
        && state.taskListData?.metadata?._id === checklistId;
      try {
        if (!checklistId || !taskId) {
          return { success: false, errorMessage: 'Task ID is required' };
        }

        const response = await getAxios().post(
          `/v2/financialclose/analysis/deletechecklist/${checklistId}/c/${taskId}`,
          {}
        );

        if (response?.data?.status !== 'success') {
          return {
            success: false,
            errorMessage: response?.data?.message || 'Unknown error',
          };
        }
      } catch (err) {
        console.error('[tasks]: deleteChecklistItem error', err);
        return {
          success: false,
          errorMessage: err?.response?.data?.message || err?.message || 'Unknown error!',
        };
      }

      // A delete shifts server offsets. The row-only path keeps the old page plan,
      // so it is safe only after every page is in. An incomplete load restarts.
      const loadComplete = state.tasklistLoad.status === TASKLIST_LOAD_STATUS.COMPLETE
        && String(state.tasklistLoad.cycleId || '') === String(checklistId);
      if (rowOnly && loadComplete) {
        commit('REMOVE_TASKLIST_ROW', { checklistId, taskId });
        if (state.currentTask && state.currentTask._id === taskId) {
          commit('SET_CURRENT_TASK', null);
        }
        try {
          await dispatch('refreshTasklistDetails', { checklistId });
          return { success: true };
        } catch (refreshErr) {
          console.error('[tasks]: deleteChecklistItem summary refresh error', refreshErr);
          return {
            success: true,
            refreshError: refreshErr?.message || 'Task was deleted, but the list could not be refreshed.',
          };
        }
      }

      try {
        await dispatch('getAllTasks', { checklistId, forceRefresh: true, progressive: true });
        return { success: true };
      } catch (refreshErr) {
        console.error('[tasks]: deleteChecklistItem refresh error', refreshErr);
        const current = state.taskListData;
        if (current && Array.isArray(current.checklist)) {
          commit('SET_TASKLIST_DATA', {
            ...current,
            checklist: current.checklist.filter((task) => task && task._id !== taskId),
          });
        }
        if (state.currentTask && state.currentTask._id === taskId) {
          commit('SET_CURRENT_TASK', null);
        }
        return {
          success: true,
          refreshError: refreshErr?.message || 'Task was deleted, but the list could not be refreshed.',
        };
      }
    },
    /**
     * Update preparer/approver assignment for a single checklist task.
     * `checklistId` is the cycle/project id. editchecklist reads it as `project`
     * (fcAccess cycle id and editChecklist project scope).
     * @param {Object} payload { taskId, checklistId, preparer: string[], approver: string[] }
     * @returns {Object} { success, errorMessage? }
     */
    async editChecklistAssignment({ commit }, { taskId, preparer, approver, checklistId }) {
      try {
        if (!taskId) {
          return { success: false, errorMessage: 'Task ID is required' };
        }
        if (!checklistId) {
          return { success: false, errorMessage: 'Project ID is required' };
        }

        const user_roles = buildCycleUserRolesFromAssignments({ preparer, approver });
        const response = await getAxios().post(
          `/v2/financialclose/analysis/editchecklist/${taskId}`,
          { data: { preparer, approver, user_roles, project: checklistId } }
        );

        if (response?.data?.status === 'success') {
          commit('UPDATE_TASK', {
            checklistId,
            taskId,
            updates: { preparer, approver, user_roles },
          });
          return { success: true };
        }

        return {
          success: false,
          errorMessage: response?.data?.message || 'Unable to update assignment',
        };
      } catch (err) {
        return {
          success: false,
          errorMessage: err?.response?.data?.message || err?.message || 'Unable to update assignment',
        };
      }
    },
    async submitTask({ commit }, { taskId, cycleId, suppressSnackbar = false }) {
      try {
        if (!cycleId || !taskId) throw new Error('Missing cycle or task ID');
        const url = `/v2/financialclose/analysis/submitTask/${taskId}/c/${cycleId}`;
        const payload = {};
        const response = await getAxios().post(url, payload);
        if (response?.data?.status === 'success') {
          if (!suppressSnackbar) {
            commit('sbSuccess', 'Task submitted successfully', { root: true });
            if (response.data.auto_approved === true && response.data.already_submitted !== true) {
              commit('sbSuccess', AUTO_APPROVE.SUBMIT_TOAST, { root: true });
            }
          }
          return { status: 'success', data: response.data };
        } else {
          if (!suppressSnackbar) {
            commit('sbError', response?.data?.message || 'Failed to submit task', { root: true });
          }
          return { status: 'error', message: response?.data?.message };
        }
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || 'Error submitting task';
        if (!suppressSnackbar) {
          commit('sbError', message, { root: true });
        }
        return { status: 'error', message };
      }
    },
    async deleteFileFromTask({ commit, rootState }, { file, task }) {
      try {
        const projectId = task.project;
        const clientId = rootState.client;
        const taskId = task._id;
        // Support both _id and file_id from file object
        const fileId = file?._id || file?.file_id;
        if (!projectId || !clientId || !taskId || !fileId) throw new Error('Missing required IDs');
        const url = `/v2/financialclose/analysis/deleteFileFromTask/${projectId}`;
        const payload = {
          client: clientId,
          data: {
            fileid: fileId,
            taskid: taskId
          }
        };
        const response = await getAxios().post(url, payload);
        if (response?.data?.status === 'success') {
          commit('sbSuccess', 'File deleted successfully', { root: true });
          return { success: true };
        } else {
          commit('sbError', response?.data?.message || 'Failed to delete file', { root: true });
          return { success: false, message: response?.data?.message };
        }
      } catch (err) {
        commit('sbError', err?.message || 'Error deleting file', { root: true });
        return { success: false, message: err?.message };
      }
    },
    async getUsersFormData({ commit }) {
      try {
        const response = await getAxios().post("/v2/financialclose/analysis/getusersformdata");
        if (response.data.status === "success") {
          // Extract users array from response
          const users = (response.data.data && response.data.data[0] && response.data.data[0].users) || [];
          commit('SET_USERS_LIST', users);
        } else {
          commit('SET_USERS_LIST', []);
          commit('sbError', response.data.message || "Failed to fetch users", { root: true });
        }
      } catch (err) {
        commit('SET_USERS_LIST', []);
        commit('sbError', err.message || "Failed to fetch users", { root: true });
      }
    },
    /**
     * Fetch enabled locations for dropdown
     * @returns {Object} { success, data }
     */
    async fetchEnabledLocations({ commit }) {
      try {
        const response = await getAxios().post("/v2/financialclose/location/getenabledlocation");
        if (response.data.status === "success") {
          const locations = response.data.data || [];
          commit('SET_ENABLED_LOCATIONS', locations);
          return { success: true, data: locations };
        } else {
          commit('SET_ENABLED_LOCATIONS', []);
          commit('sbError', response.data.message || "Failed to fetch enabled locations", { root: true });
          return { success: false };
        }
      } catch (err) {
        console.error('[StoreAction]: fetchEnabledLocations error', err);
        commit('SET_ENABLED_LOCATIONS', []);
        commit('sbError', err?.response?.data?.message || err?.message || "Failed to fetch enabled locations", { root: true });
        return { success: false };
      }
    },
    /**
     * Fetch client-level reference data via /v3/ consolidated endpoint.
     * Caches result in store — returns cached data unless forceRefresh is true.
     * Replaces: T1 (getAllUsersForEntity), T2 (getenabledlocation), T4 (getformdata),
     *           T5 (location/get), T8 (chartofaccount/get), T9 (uniquecategories), T10 (client/get)
     * @param {Object} payload { forceRefresh }
     * @returns {Object} { users, locations, actorLocations, coaCategoryAcc, coaSubcategoryAcc, categoryAcc, subcategoryAcc, schedulerEnabled, horizonViewEnabled, exportManagerEnabled }
     */
    async fetchFormDataV3({ commit, state, rootState }, { forceRefresh = false } = {}) {
      if (!forceRefresh && state.formDataV3) {
        // locations = full name catalog; actorLocations = dropdown / eligibility set
        commit(
          'SET_ENABLED_LOCATIONS',
          (state.formDataV3.actorLocations || []).filter(location => location && location.enabled !== false)
        );
        return state.formDataV3;
      }
      if (!forceRefresh && formDataV3Promise) {
        return await formDataV3Promise;
      }

      const fetchFormData = async () => {
        try {
          const clientId = rootState.client;
          if (!clientId) return null;
          const response = await getAxios().post('/v3/financialclose/analysis/getformdata', { client: clientId });
          if (response?.data?.status === 'success') {
            const formData = response.data.data;
            commit('SET_FORM_DATA_V3', formData);
            // Also populate legacy state slices for backward compatibility
            const allUsers = formData.users || [];
            const activeUsers = allUsers.filter(u => u.isActive);
            const inactiveUsers = allUsers.filter(u => !u.isActive);
            // Enabled/dropdown set is actor-scoped; full catalog stays on formData.locations
            const availableLocations = (formData.actorLocations || []).filter(
              location => location && location.enabled !== false
            );
            commit('SET_ACTIVE_USERS', activeUsers);
            commit('SET_INACTIVE_USERS', inactiveUsers);
            commit('SET_ENABLED_LOCATIONS', availableLocations);
            commit('SET_NOTIFICATION_ENABLED', formData.schedulerEnabled || false);
            return formData;
          } else {
            throw new Error(response?.data?.message || 'Failed to fetch form data');
          }
        } catch (err) {
          console.error('[StoreAction]: fetchFormDataV3 error', err);
          commit('sbError', err?.response?.data?.message || err?.message || 'Failed to fetch reference data', { root: true });
          return null;
        }
      };

      const inflight = fetchFormData();
      let ownsSharedPromise = false;
      if (!forceRefresh) {
        formDataV3Promise = inflight;
        ownsSharedPromise = true;
      }

      try {
        return await inflight;
      } finally {
        if (ownsSharedPromise) {
          formDataV3Promise = null;
        }
      }
    },
    /**
     * Ensure filteredLocations are populated in the store.
     * If already available and matching the cycleId, no-op.
     * Otherwise fetches locations via v3 formdata + cycle metadata to compute the filtered set.
     * Used by Threshold and Dashboard pages that may load independently of Tasklist.
     * @param {Object} payload { cycleId }
     */
    async ensureFilteredLocations({ commit, state, dispatch }, { cycleId }) {
      // If already populated for this cycle, no-op
      if (state.filteredLocations && state.filteredLocations.length > 0 && state.filteredLocationsCycleId === cycleId) return;

      try {
        // Actor-scoped locations from v3 formdata (cached); catalog stays on formData.locations
        const formData = state.formDataV3 || await dispatch('fetchFormDataV3');
        const actorLocations = formData?.actorLocations || [];

        // Get cycle locations from cached metadata or fetch minimal cycle info
        let cycleLocations = [];
        const metadata = state.taskListData?.metadata;
        if (metadata && metadata._id === cycleId) {
          cycleLocations = metadata.location || [];
        } else {
          const res = await getAxios().post(`/v3/financialclose/analysis/getdetails/${cycleId}`, { page: 'tasklist' });
          if (res.data?.status === "success") {
            cycleLocations = res.data.data?.metadata?.location || [];
          }
        }

        const filtered = cycleLocations.length === 0
          ? actorLocations.map(loc => ({ _id: loc._id, name: loc.name }))
          : actorLocations.filter(loc => cycleLocations.includes(loc._id))
                         .map(loc => ({ _id: loc._id, name: loc.name }));
        commit('SET_FILTERED_LOCATIONS', { locations: filtered, cycleId });
      } catch (err) {
        console.error('[StoreAction]: ensureFilteredLocations error', err);
      }
    },
  /**
   * Bulk upload files for tasks (from TaskUploadModal.vue)
   * @param {Object} payload { files, id, selectItem, viewtaskdata, remark, suppressSnackbar }
   */
    async uploadFilesBulk({ commit, state }, { files, id, selectItem, viewtaskdata, remark, suppressSnackbar = false }) {
      let totalSize = 0;
      let dataArrayList = [];
      try {
        if (Array.isArray(files) && files.length > 0) {
          files.forEach((file) => {
            if (file.name) {
              totalSize += file.size;
            }
          });
          const MAX_TOTAL_SIZE = 25 * 1024 * 1024;
          if (totalSize > MAX_TOTAL_SIZE) {
            commit('sbError', `Total file size exceeds the limit (${(MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(2)} MB)`, { root: true });
            return;
          }
          const $novaLocal = getNova();
          if (!$novaLocal || typeof $novaLocal.uploadFile !== 'function') {
            commit('sbError', 'File upload service not initialized', { root: true });
            return;
          }
          const uploadPromises = files.map(async (file) => {
            if (file.name) {
              try {
                let response = await $novaLocal.uploadFile(getAxios(), file, {});
                dataArrayList.push(response);
              } catch (uploadError) {
                commit('sbError', uploadError.message || 'Error uploading file', { root: true });
                console.error(uploadError);
              }
            }
          });
          await Promise.all(uploadPromises);
          if (dataArrayList.some((response) => response.data.status === 'success')) {
            const dataArray = dataArrayList.flatMap((item) =>
              item.data.data.flatMap((item) => {
                return {
                  filename: item.uploadname,
                  file_id: item._id,
                  file_path: item.path,
                };
              })
            );
            // Build checklist list (full objects as in legacy Tasklist.vue implementation)
            const selectedTasksRaw = (Array.isArray(selectItem) && selectItem.length)
              ? selectItem
              : (viewtaskdata ? [viewtaskdata] : []);

            // If only IDs are desired (lean payload), map to IDs. For now keep full objects to match old working flow.
            // To switch to IDs later, replace checklist: selectedTasksRaw with checklist: selectedTasksRaw.map(t=>t && t._id).filter(Boolean)
            const data = {
              bulkupload: true,
              upload: dataArray,
              project: id,
              checklist: selectedTasksRaw,
              remark: remark,
              removelocalfile: false,
            };
            try {
              let response = await getAxios().post(
                '/v2/financialclose/analysis/bulkStoreWorkPaper',
                { data: [data] }
              );
              if (response?.data?.status === 'success') {
                if (!suppressSnackbar) {
                  commit('sbSuccess', 'Files uploaded', { root: true });
                }
                commit('RESET_BULK_UPLOAD_UI');
                // Apply reconciliation flag (deduplicated logic)
                const reconTargets = (selectItem && selectItem.length ? selectItem : [viewtaskdata]);
                applyReconFlag({ commit, state }, { responseData: response.data, tasks: reconTargets, checklistId: id });
                return { success: true, data: response.data };
              } else {
                throw new Error(response?.data?.message || 'Error Uploading File');
              }
            } catch (axiosError) {
              const message = axiosError?.response?.data?.message || axiosError?.message || 'Unknown error!';
              commit('sbError', message, { root: true });
              return { success: false, error: message };
            }
          } else {
            commit('sbError', 'Unable to upload file', { root: true });
            return { success: false, error: 'upload_failed' };
          }
        } else {
          commit('sbError', 'No files to upload', { root: true });
          return { success: false, error: 'no_files' };
        }
      } catch (error) {
        console.error(error);
        return { success: false, error: error.message };
      } finally {
        commit('RESET_BULK_UPLOAD_UI');
      }
    },
  /**
   * Get conversation/comments for a task (from TaskDetailsTabbed.vue)
   * @param {Object} payload { metadataId, taskId }
   */
    async getConversation({ commit }, { metadataId, taskId }) {
      if (!metadataId || !taskId) {
        commit('sbError', 'Missing metadataId or taskId for getConversation', { root: true });
        return { success: false, error: 'Missing metadataId or taskId' };
      }
      let url = `/v2/financialclose/analysis/getconversation/${metadataId}/c/${taskId}`;
      try {
        const dt = await getAxios().post(url);
        if (dt.data.status === 'success') {
          // Store comments in the specific task object in state
          const comments = dt.data.data;
          // Update in tasks cache
          if (comments && Array.isArray(comments)) {
            if (this.tasks && this.tasks[taskId]) {
              this.tasks[taskId].comments = comments;
            }
            // Also update currentTask if it's the same
            if (this.currentTask && this.currentTask._id === taskId) {
              this.currentTask.comments = comments;
            }
          }
          return { success: true, data: comments };
        } else {
          throw new Error(dt.data.message || 'Unknown error');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error(err);
        return { success: false, error: err.message || err || 'Unknown error!' };
      }
    },
  /**
   * Add a conversation/comment to a task (from Comments.vue)
   * @param {Object} payload { metadataId, taskId, dataObject }
   */
    async addConversation({ commit }, { metadataId, taskId, dataObject }) {
      if (!metadataId || !taskId) {
        commit('sbError', 'Missing metadataId or taskId for conversation', { root: true });
        return { success: false };
      }
      let url = `/v2/financialclose/analysis/addconversation/${metadataId}/c/${taskId}`;
      try {
        const dt = await getAxios().post(url, { data: dataObject });
        if (dt.data.status === 'success') {
          return { success: true };
        } else {
          throw new Error(dt.data.message || 'Unknown error');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error(err);
        return { success: false };
      }
    },
  /**
   * Send for review (single task) - moved from TaskUploadModal.vue
   * @param {Object} payload { id, viewtaskdata, remark }
   */
    async sendForReview({ commit }, { id, viewtaskdata, remark, remarkOnly = false, suppressSnackbar = false }) {
      if (!viewtaskdata || !viewtaskdata._id) {
        commit('sbError', 'Task data is missing or invalid.', { root: true });
        return { error: 'Task data is missing or invalid.' };
      }
      let url = `/v2/financialclose/analysis/sendforreview/${id}/c/${viewtaskdata._id}`;
      try {
        const dt = await getAxios().post(url, { data: { remark, remarkOnly } });
        if (dt.data.status === 'success') {
          if (!suppressSnackbar) {
            commit('sbSuccess', BULK_UPLOAD_MESSAGES.REMARKS_SAVED, { root: true });
          }
          return { success: true };
        } else {
          // Return backend error message
          return { error: dt.data.message || 'Unknown error' };
        }
      } catch (err) {
        console.error('[tasks/sendForReview]:', err);
        return { error: err?.response?.data?.message || err.message || err || 'Unknown error!' };
      }
   },

  /**
   * Send for review (bulk) - moved from TaskUploadModal.vue
   * @param {Object} payload { id, selectItem, localFileremark, remarkOnly, viewtaskdata }
   */
    async sendForReviewBulk({ commit, dispatch }, {
      id,
      selectItem,
      localFileremark,
      remarkOnly = false,
      suppressSnackbar = false,
    }) {
      if (remarkOnly) {
        const tasks = (selectItem || []).filter((item) => item && item._id);
        for (const item of tasks) {
          const result = await dispatch('sendForReview', {
            id,
            viewtaskdata: item,
            remark: localFileremark,
            remarkOnly: true,
            suppressSnackbar: true,
          });
          if (!result?.success) {
            commit('sbError', result?.error || 'Error Adding Remarks.', { root: true });
            return { success: false };
          }
        }
        if (!suppressSnackbar) {
          commit('sbSuccess', BULK_UPLOAD_MESSAGES.REMARKS_SAVED, { root: true });
        }
        return { success: true };
      }

      const tasklistid = (selectItem || []).map((item) => item._id);
      let url = '/v2/financialclose/analysis/bulksendforreview';
      try {
        const dt = await getAxios().post(url, {
          data: { remark: localFileremark, pid: id, cidList: tasklistid },
        });
        if (dt.data.status === 'success') {
          if (!suppressSnackbar) {
            commit('sbSuccess', BULK_UPLOAD_MESSAGES.SENT_FOR_REVIEW, { root: true });
          }
          return { success: true };
        }
        throw new Error(dt.data.message || 'Unknown error');
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error(err);
        return { success: false };
      }
    },
  /**
   * Upload files for a task (moved from TaskUploadModal.vue)
   * @param {Object} payload { files, taskData, projectId, isFileValid }
   */
    async uploadFiles({ commit, state }, { files, taskData, remark, projectId, isFileValid, suppressSnackbar = false }) {
      // Validate files
      let invalidFiles = (files || []).filter(f => !isFileValid(f));
      let totalSize = 0;
      (files || []).forEach(f => { totalSize += f.size || 0; });
      const MAX_TOTAL_SIZE = 25 * 1024 * 1024;
      if (totalSize > MAX_TOTAL_SIZE) {
        commit('sbError', `Total file size exceeds the limit (${(MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(2)} MB)`, { root: true });
        return;
      }
      if (invalidFiles.length > 0) {
        commit('sbError', 'Cannot upload: One or more selected files are invalid. Please remove invalid files before uploading.', { root: true });
        return;
      }
      let arrayPush = [];
      let validFiles = files.filter(f => isFileValid(f));
      if (Array.isArray(validFiles) && validFiles.length > 0) {
      const $novaLocal = getNova();
      if (!$novaLocal || typeof $novaLocal.uploadFile !== 'function') {
          commit('sbError', 'File upload service not initialized', { root: true });
          return;
        }
        const uploadPromises = validFiles.map(async (file) => {
          try {
            let dt = await $novaLocal.uploadFile(getAxios(), file, {});
            arrayPush.push(dt);
          } catch (err) {
            commit('sbError', err.message || err || 'Unknown error!', { root: true });
            console.error(err);
          }
        });
        await Promise.all(uploadPromises);
        if (arrayPush.some((response) => response.data.status === 'success')) {
          let dataArray = arrayPush.flatMap((response) =>
            response.data.data.map((item) => {
              return {
                filename: item.uploadname,
                file_id: item._id,
                file_path: item.path,
              };
            })
          );
          let data = {
            upload: dataArray,
            project: projectId,
            checklist: taskData,
            bulkupload: false,
            remark: remark || '',
          };
          try {
            let response = await getAxios().post(
              '/v2/financialclose/analysis/storeworkpaper',
              {
                data: [data],
              }
            );
            if (response?.data?.status === 'success') {
              if (!suppressSnackbar) {
                commit('sbSuccess', 'File Uploaded', { root: true });
              }
              commit('SET_DRAWER_VISIBLE', false);
              commit('SET_SEND_FOR_REVIEW_DRAWER', false);
              // Apply reconciliation flag (deduplicated logic)
              const checklistId = taskData && (taskData.project || taskData.checklist_id || taskData.project_id);
              applyReconFlag({ commit, state }, { responseData: response.data, tasks: taskData, checklistId });
            } else {
              throw new Error(response?.data?.message || 'Error uploading file');
            }
          } catch (axiosError) {
            commit('sbError', axiosError?.response?.data?.message || axiosError?.message || 'Unknown error!', { root: true });
            console.error(axiosError);
          }
        } else {
          commit('sbError', 'Unable to upload file', { root: true });
        }
      } else {
        commit('sbError', 'No valid files to upload', { root: true });
      }
    },
  /**
   * Change checklist status (approve/reject) for a task
   * @param {{ checklistId: string, taskId: string, status: string }} payload
   */
    async changeChecklistStatus({ commit }, { checklistId, taskId, status, remark, approver_remark }) { 
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      let idtxt = taskId;
      const rawRemark = remark ?? approver_remark;
      const trimmedRemark = typeof rawRemark === 'string' ? rawRemark.trim() : '';
      if (status === CHECKLIST_STATUS_ACTION.REJECT && !isValidRejectionRemark(trimmedRemark)) {
        const message = trimmedRemark ? REJECTION_REMARK.INVALID : REJECTION_REMARK.REQUIRED;
        commit('sbError', message, { root: true });
        commit('SET_LOADING', false);
        return false;
      }
      if (status === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING && !isValidRejectionRemark(trimmedRemark)) {
        const message = trimmedRemark ? REJECTION_REMARK.INVALID : REJECTION_REMARK.REQUIRED;
        commit('sbError', message, { root: true });
        commit('SET_LOADING', false);
        return false;
      }
      const data = { type: status };
      if (
        trimmedRemark
        || status === CHECKLIST_STATUS_ACTION.REJECT
        || status === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING
        || status === CHECKLIST_STATUS_ACTION.APPROVE
        || status === CHECKLIST_STATUS_ACTION.SIGNOFF
      ) {
        Object.assign(data, buildChecklistStatusRemarkPayload(status, trimmedRemark));
      }
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/changecheckliststage/${checklistId}/a/${idtxt}`,
          { data: [data] }
        );
        if (response.data.status === "success") {
          commit("sbSuccess", "Status Updated", { root: true });
          return true;
        } else {
          throw response.data.message || "Error changing status";
        }
      } catch (err) {
        commit("sbError", err.message || err || "Unknown error!", { root: true });
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    /**
     * Bulk approve/reject checklist tasks.
     * @param {{ checklistId: string, taskIds: string[], status: string, remark?: string }} payload
     */
    async bulkChangeChecklistStatus({ commit }, { checklistId, taskIds, status, remark }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      const trimmedRemark = typeof remark === 'string' ? remark.trim() : '';
      if (
        (status === CHECKLIST_STATUS_ACTION.REJECT
          || status === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING)
        && !isValidRejectionRemark(trimmedRemark)
      ) {
        const message = trimmedRemark ? REJECTION_REMARK.INVALID : REJECTION_REMARK.REQUIRED;
        commit('sbError', message, { root: true });
        commit('SET_LOADING', false);
        return false;
      }
      const data = {
        type: status,
        ...buildChecklistStatusRemarkPayload(status, trimmedRemark),
        checklist: taskIds,
      };
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/changecheckliststage/${checklistId}/a/bulk`,
          { data: [data] }
        );
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Status Updated', { root: true });
          return true;
        }
        throw response.data.message || 'Error changing status';
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchCustomLayout({ commit, state }, { pageName, forceRefresh = false }) {
      if (!forceRefresh && Object.prototype.hasOwnProperty.call(state.customLayouts, pageName)) {
        return state.customLayouts[pageName];
      }
      const payload = { page: pageName };
      const response = await getAxios().post(
        "/v2/financialclose/analysis/getcustomlayout",
        { data: payload }
      );
      if (response?.data?.status === "success" && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const layoutData = { ...response.data.data[0], page: pageName };
        commit('SET_CUSTOM_LAYOUT_PAGE', { page: pageName, data: layoutData });
        return layoutData;
      }
      commit('SET_CUSTOM_LAYOUT_PAGE', { page: pageName, data: null });
      return null;
    },
    /**
     * Preload all Tasklist page APIs into Vuex (formdata, details, checklist, custom layout).
     * Skips network calls when cached data is already available unless forceRefresh is true.
     */
    async ensureTasklistPageData({ dispatch }, { checklistId, pageName, forceRefresh = false, progressive = false }) {
      const [formData, taskListData, customLayout] = await Promise.all([
        dispatch('fetchFormDataV3', { forceRefresh }),
        dispatch('getAllTasks', { checklistId, forceRefresh, progressive }),
        dispatch('fetchCustomLayout', { pageName, forceRefresh }),
      ]);
      return { formData, taskListData, customLayout };
    },
    async fetchStatusDetails({ commit }, { metadataId }) {
      const response = await getAxios().post(
        `/v2/financialclose/analysis/getduedayswisecount/${metadataId}`
      );
      if (response?.data?.status === "success" && Array.isArray(response.data.data) && response.data.data.length > 0) {
        commit('SET_STATUS_DETAILS', response.data.data[0]);
        return response.data.data[0];
      } else {
        commit('SET_STATUS_DETAILS', null);
        return null;
      }
    },
    async fetchTransactionMatchingAnalysis({ commit }, { checklistId }) {
      const response = await getAxios().post(
        `/v2/bot/analysis/getTransactionMatchingAnalysis/${checklistId}`
      );
      if (response?.data?.status === "success" && Array.isArray(response.data.data) && response.data.data.length > 0) {
        commit('SET_TRANSACTION_MATCHING_LIST', response.data.data);
        return response.data.data;
      } else {
        commit('SET_TRANSACTION_MATCHING_LIST', null);
        return null;
      }
    },
    /**
     * Download bulk edit template for selected cycle tasklist items.
     * Returns raw axios response with blob body and headers.
     */
    async downloadBulkEditTasklistTemplate({ rootState }, { checklistId, taskIds }) {
      const cid = rootState.client || rootState.user?.client || '';
      const response = await getAxios().post(
        `/v2/financialclose/analysis/downloadBulkEditTasklistTemplate/${checklistId}`,
        { taskIds },
        {
          params: { cid },
          responseType: 'blob',
        }
      );
      return response;
    },
    /** Dry-run validation for cycle add/edit/bulk payloads (mode: add|edit|bulkAdd|bulkUpdate). */
    async validateCycleTaskList({ rootState }, { checklistId, data, mode }) {
      try {
        const client = rootState.client || rootState.user?.client || undefined;
        // Access checks read user_roles only; attach current assignees for add/edit/bulkUpdate.
        // bulkAdd still resolves emails server-side via shapeBulkAddImportRows.
        let payload = data;
        if (mode === 'add' || mode === 'edit') {
          payload = withCycleUserRoles(data);
        } else if (mode === 'bulkUpdate' && Array.isArray(data)) {
          payload = data.map((row) => withCycleUserRoles(row));
        }
        // Same cycle-list caches as editchecklist. validateCycleTaskList does not read them.
        payload = omitCycleListCachesFromPayload(payload);
        if (mode === 'edit' || mode === 'bulkUpdate') {
          payload = omitCycleTaskClientOwnedWriteFieldsFromPayload(payload);
        }
        const body = { data: payload, client };
        if (mode) body.mode = mode;
        const response = await getAxios().post(
          `/v2/financialclose/analysis/validateCycleTaskList/${checklistId}`,
          body,
        );
        return response?.data || {};
      } catch (err) {
        console.error('[StoreAction]: validateCycleTaskList error', err);
        return {
          status: 'failure',
          message: err?.response?.data?.message || err?.message || 'Bulk update validation failed',
          data: err?.response?.data?.data || { valid: false },
        };
      }
    },
    /**
     * Dry-run TB bulk import validation (GL code / location).
     * @returns {{ valid: boolean, rowErrors: Array, fieldErrors: object, message?: string }}
     */
    async validateBulkTb(_ctx, { projectId, data }) {
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/validateBulkTb/${projectId}`,
          { data },
        );
        const payload = response?.data?.data || {};
        return {
          valid: payload.valid === true,
          fieldErrors: payload.fieldErrors || {},
          rowErrors: Array.isArray(payload.rowErrors) ? payload.rowErrors : [],
          message: response?.data?.message,
        };
      } catch (err) {
        console.error('[StoreAction]: validateBulkTb error', err);
        return {
          status: 'failure',
          valid: false,
          fieldErrors: err?.response?.data?.data?.fieldErrors || {},
          rowErrors: err?.response?.data?.data?.rowErrors || [],
          message: err?.response?.data?.message || err?.message || 'Validation failed',
        };
      }
    },
    /**
     * Bulk-add cycle checklist rows (or master templates when checklistId is omitted).
     * Returns API envelope { status, message, data }.
     */
    async bulkAddCycleTaskList(_ctx, { checklistId, data }) {
      try {
        const url = checklistId
          ? `/v2/financialclose/analysis/bulkaddchecklist/${checklistId}`
          : '/v2/financialclose/masters/checklist/bulkadd';
        const response = await getAxios().post(url, { data });
        return response?.data || {};
      } catch (err) {
        console.error('[StoreAction]: bulkAddCycleTaskList error', err);
        return {
          status: 'failure',
          message: err?.response?.data?.message || err?.message || 'Error uploading Tasklist',
          data: err?.response?.data?.data,
        };
      }
    },
    /**
     * Bulk-update existing cycle checklist rows from Excel.
     * Returns API envelope { status, message, data }.
     */
    async bulkUpdateCycleTaskList({ rootState }, { checklistId, data }) {
      try {
        const client = rootState.client || rootState.user?.client || undefined;
        const withRoles = Array.isArray(data) ? data.map((row) => withCycleUserRoles(row)) : data;
        const payload = omitCycleTaskClientOwnedWriteFieldsFromPayload(
          omitCycleListCachesFromPayload(withRoles),
        );
        const response = await getAxios().post(
          `/v2/financialclose/analysis/bulkUpdateCycleTaskList/${checklistId}`,
          { data: payload, client },
        );
        return response?.data || {};
      } catch (err) {
        console.error('[StoreAction]: bulkUpdateCycleTaskList error', err);
        return {
          status: 'failure',
          message: err?.response?.data?.message || err?.message || 'Error uploading Tasklist',
          data: err?.response?.data?.data,
        };
      }
    },
    /**
     * Validate (when checklistId is set) then write cycle bulk add/update.
     * mode: 'bulkAdd' | 'bulkUpdate'. Skips validate when no checklistId (master bulk add).
     * @returns {{ ok: boolean, rowErrors: Array, message: string, validation?: object, writeData?: object }}
     */
    async validateAndWriteCycleTaskList({ dispatch }, { checklistId, data, mode }) {
      let validation = null;
      if (checklistId) {
        validation = await dispatch('validateCycleTaskList', {
          checklistId,
          data,
          mode,
        });
        const parsed = parseCycleTaskValidateResult(validation);
        if (!parsed.valid) {
          return {
            ok: false,
            validation,
            rowErrors: parsed.rowErrors,
            message: parsed.message || (mode === 'bulkUpdate'
              ? 'Bulk update validation failed'
              : 'Bulk add validation failed'),
          };
        }
      }
      const writeData = mode === 'bulkUpdate'
        ? await dispatch('bulkUpdateCycleTaskList', { checklistId, data })
        : await dispatch('bulkAddCycleTaskList', { checklistId, data });
      return {
        ok: true,
        validation,
        writeData,
        rowErrors: [],
        message: writeData?.message || '',
      };
    },
    /**
     * Fetch all tasks and related data for a checklist (Tasklist page)
     * Phase 2: Uses split APIs — /v3/getdetails (metadata/summary) + /v3/getchecklist (tasks).
     * getdetails page tasklist returns cycle metadata, role summaries, filtered
     * locations, and status counts. Custom columns come from form data.
     * getDetails is now slimmed (no checklist[], no tb[]).
     * checklist is fetched separately via fetchChecklist.
     * Eliminates client-side calculateDetails() and the 7-API waterfall.
     * With TASKLIST_PROGRESSIVE_LOAD on, getchecklist page 1 is committed first and
     * the other pages append in the background. `progressive: true` (tasklist page)
     * resolves after page 1; other callers wait until every page is in.
     * `preserveUntilComplete: true` (task detail refresh when a list is already in
     * memory) builds the next checklist beside the current one and assigns it
     * only when every page is in. It does not call getdetails and does not set
     * state.loading. A cold load omits the flag: getdetails is written onto
     * taskListData as soon as it returns, and getchecklist continues after that.
     * `detailsPromise` on the in-flight record resolves when metadata._id is stored.
     */
    async getAllTasks({ commit, dispatch, state, rootState }, {
      checklistId,
      forceRefresh = false,
      progressive = false,
      preserveUntilComplete = false,
    }) {
      if (!rootState.client) return null;
      const settle = async (data) => {
        if (progressive || !data) return data;
        await dispatch('waitForTasklistComplete', { checklistId });
        return state.taskListData;
      };
      // Join an in-flight fetch before reading cache so create-then-View
      // does not resolve the pre-create checklist and miss the new task.
      // A task-detail reload keeps the current list until it swaps. Back should
      // read that list now, not wait for every checklist page.
      if (!forceRefresh && getAllTasksInflight?.checklistId === checklistId) {
        if (getAllTasksInflight.preserveUntilComplete) {
          return state.taskListData;
        }
        return settle(await getAllTasksInflight.promise);
      }
      const sameCycle = String(state.taskListData?.metadata?._id || '') === String(checklistId)
        && String(state.tasklistLoad.cycleId || '') === String(checklistId);
      const hasStoredList = state.taskListData?.metadata?._id != null
        && String(state.taskListData.metadata._id) === String(checklistId);
      if (!shouldRefetchTasklist({
        forceRefresh,
        sameCycle,
        hasStoredList,
        status: state.tasklistLoad.status,
        progressive,
      })) {
        return settle(state.taskListData);
      }

      let detailsSettled = false;
      let resolveDetails;
      let rejectDetails;
      const detailsPromise = new Promise((resolve, reject) => {
        resolveDetails = resolve;
        rejectDetails = reject;
      });
      // Callers that only await the checklist promise still leave this settled.
      detailsPromise.catch(() => {});
      const metadataMatches = () => {
        const id = state.taskListData?.metadata?._id;
        return id != null && String(id) === String(checklistId);
      };
      const publishDetails = () => {
        if (detailsSettled || !metadataMatches()) return;
        detailsSettled = true;
        resolveDetails(state.taskListData);
      };
      const failDetails = (err) => {
        if (detailsSettled) return;
        detailsSettled = true;
        rejectDetails(err instanceof Error ? err : new Error(err?.message || 'No analysis data'));
      };
      let detailsRequestDone = preserveUntilComplete === true;

      const fetchAllTasks = async () => {
        const preserveList = preserveUntilComplete === true;
        const previousLoad = preserveList ? { ...state.tasklistLoad } : null;
        if (!preserveList) {
          commit('SET_LOADING', true);
          commit('SET_ERROR', null);
        }
        const token = beginTasklistLoad(commit, state, checklistId);
        if (
          preserveList
          && previousLoad
          && String(previousLoad.cycleId || '') === String(checklistId)
        ) {
          // beginTasklistLoad marks the list loading and zeroes its counts.
          // Put the current list's status back so Back still shows every row.
          commit('SET_TASKLIST_LOAD', {
            status: previousLoad.status,
            loaded: previousLoad.loaded,
            total: previousLoad.total,
            error: previousLoad.error,
          });
        }
        const useProgressive = TASKLIST_PROGRESSIVE_LOAD.ENABLED;
        try {
          // Task detail already awaits v3 getdetails. This reload is getchecklist only.
          const checklistRequest = dispatch('fetchChecklist', {
            checklistId,
            body: useProgressive ? firstPageBody(TASKLIST_PROGRESSIVE_LOAD) : {},
            signal: token.signal,
          });
          let analysisResponse = null;
          let checklistResult;
          if (preserveList) {
            checklistResult = await checklistRequest;
          } else {
            // Store metadata as soon as getdetails returns. getchecklist keeps running.
            const detailsRequest = getAxios()
              .post(`/v3/financialclose/analysis/getdetails/${checklistId}`, { page: 'tasklist' })
              .then((response) => {
                detailsRequestDone = true;
                if (!isCurrentLoad(state.tasklistLoad, token)) {
                  failDetails(new Error('No analysis data'));
                  return response;
                }
                const dt = response?.data;
                if (dt?.status !== 'success' || !dt.data) {
                  failDetails(new Error(dt?.message || 'No analysis data'));
                  return response;
                }
                const data = dt.data;
                commit('SET_TASKLIST_DETAILS', {
                  checklistId,
                  metadata: data.metadata ? tasklistDetailsMetadata(data.metadata) : null,
                  summary: data.summary || null,
                });
                commit('SET_FILTERED_LOCATIONS', {
                  locations: data.filteredLocations || [],
                  cycleId: checklistId,
                });
                commit('SET_STATUS_DETAILS', data.statusDetails || null);
                publishDetails();
                return response;
              })
              .catch((err) => {
                detailsRequestDone = true;
                failDetails(err);
                throw err;
              });
            [analysisResponse, checklistResult] = await Promise.all([
              detailsRequest,
              checklistRequest,
            ]);
          }
          // A newer load (cycle change, refresh, leave) owns the store now.
          if (!isCurrentLoad(state.tasklistLoad, token)) return state.taskListData;

          let metadata;
          let summary;
          let filteredLocations;
          let statusDetails;
          if (preserveList) {
            const latest = state.taskListData || {};
            // Keep a real cycle record. Do not replace null metadata with {} —
            // comments treat a missing _id as a failed getConversation.
            metadata = latest.metadata && latest.metadata._id ? latest.metadata : null;
            summary = latest.summary || { manager: {}, preparer: {}, approver: {} };
            filteredLocations = state.filteredLocations || [];
            statusDetails = state.statusDetails || null;
          } else {
            const dt = analysisResponse.data;
            if (dt.status !== 'success' || !dt.data) throw new Error('No analysis data');
            const data = dt.data;
            metadata = data.metadata ? tasklistDetailsMetadata(data.metadata) : {};
            summary = data.summary || { manager: {}, preparer: {}, approver: {} };
            filteredLocations = data.filteredLocations || [];
            statusDetails = data.statusDetails || null;
          }

          // Use checklist and schema from the dedicated endpoint
          let tasklistData = checklistResult.checklist || [];
          let customColumns = checklistResult.customColumns || {};
          let customColumnSchema = checklistResult.customColumnSchema || [];
          console.log('[getAllTasks] customColumnSchema from fetchChecklist:', customColumnSchema.length, 'entries');

          if (tasklistData.length === 0) {
            console.warn('[getAllTasks] API returned empty checklist.');
          }

          // Get formdata from cache or fetch (provides locations, users, categories)
          const formData = state.formDataV3 || await dispatch('fetchFormDataV3');
          const locationList = formData?.locations || [];
          const usersList = (formData?.users || []).filter(u => u.isActive);
          // COA-only categories (for Account Reconciliation tasks)
          const coaCategoryAcc = formData?.coaCategoryAcc || [];
          const coaSubcategoryAcc = formData?.coaSubcategoryAcc || [];
          // Merged categories: COA + free-text (for Normal/Checklist tasks)
          const categoryAcc = formData?.categoryAcc || [];
          const subcategoryAcc = formData?.subcategoryAcc || [];

          // This fetch replaces the stored checklist. SET_TASKLIST_DATA assigns
          // state.taskListData = data, so a cycle change does not append.
          // A task-detail reload holds that assignment until every page is in.
          const checklistForCommit = freezeTaskListReadOnlyNests(
            taskChecklistForCommit(tasklistData)
          );

          let rowsForCommit = checklistForCommit;
          let columnsForCommit = customColumns;
          if (preserveList && useProgressive) {
            const bufferedTotal = Math.max(Number(checklistResult.totalCount) || 0, rowsForCommit.length);
            const bufferedPlan = buildRemainingPagePlan(bufferedTotal, TASKLIST_PROGRESSIVE_LOAD);
            if (bufferedPlan.length) {
              console.log('[tasks]: tasklist buffered load', {
                firstCount: rowsForCommit.length,
                totalCount: bufferedTotal,
                pages: bufferedPlan.length,
              });
              const gathered = await gatherChecklistBesideCurrent({
                token,
                state,
                firstRows: rowsForCommit,
                customColumns: columnsForCommit,
                plan: bufferedPlan,
              });
              if (gathered.stale || !isCurrentLoad(state.tasklistLoad, token)) return state.taskListData;
              rowsForCommit = gathered.checklist;
              columnsForCommit = gathered.customColumns;
            }
          }

          const cacheData = {
            metadata,
            checklist: rowsForCommit,
            pbcitems: [], // PBC no longer returned by v3
            summary,
            locationList,
            usersList,
            locationThresholds: [], // Thresholds now embedded in __computed per task
            infomangerrequests: [], // PBC requests removed from tasklist flow
            chartAcc: [], // Categories now come from formdata
            coaCategoryAcc,
            coaSubcategoryAcc,
            categoryAcc,
            subcategoryAcc,
            customColumns: columnsForCommit,
            customColumnSchema
          };
          if (preserveList) {
            const latest = state.taskListData || {};
            if (latest.metadata && latest.metadata._id) cacheData.metadata = latest.metadata;
            if (latest.summary) cacheData.summary = latest.summary;
          }
          commit('SET_TASKLIST_DATA', cacheData);
          if (!preserveList) {
            commit('SET_FILTERED_LOCATIONS', { locations: filteredLocations, cycleId: checklistId });
            commit('SET_STATUS_DETAILS', statusDetails);
          }
          commit('SET_TRANSACTION_MATCHING_LIST', null); // TM data embedded per-task now

          // schedulerEnabled comes from formdata (already set in fetchFormDataV3)
          if (!state.notificationEnabled && formData?.schedulerEnabled) {
            commit('SET_NOTIFICATION_ENABLED', formData.schedulerEnabled);
          }

          if (preserveList) {
            commit('SET_TASKLIST_LOAD', {
              loaded: rowsForCommit.length,
              total: rowsForCommit.length,
              status: TASKLIST_LOAD_STATUS.COMPLETE,
              error: null,
              revision: Date.now(),
            });
          } else {
            const firstCount = checklistForCommit.length;
            const totalCount = useProgressive
              ? Math.max(Number(checklistResult.totalCount) || 0, firstCount)
              : firstCount;
            const plan = useProgressive ? buildRemainingPagePlan(totalCount, TASKLIST_PROGRESSIVE_LOAD) : [];
            commit('SET_TASKLIST_LOAD', {
              loaded: firstCount,
              total: totalCount,
              status: plan.length ? TASKLIST_LOAD_STATUS.LOADING : TASKLIST_LOAD_STATUS.COMPLETE,
            });
            if (plan.length) {
              console.log('[tasks]: tasklist progressive load', { firstCount, totalCount, pages: plan.length });
              startRemainingPages({ commit, state }, {
                token,
                plan,
                pages: plan,
                buffer: createPageBuffer(checklistForCommit),
              });
            }
          }

          return cacheData;
        } catch (err) {
          if (!isCurrentLoad(state.tasklistLoad, token) || isRequestCanceled(err)) {
            return state.taskListData;
          }
          // A failed detail reload must not stay COMPLETE: fetchTask does not
          // patch checklist rows, and progressive callers also reuse ERROR.
          // Keep the rows already on screen and make the next visit refetch.
          if (preserveList) {
            commit('SET_TASKLIST_LOAD', {
              status: TASKLIST_LOAD_STATUS.STALE,
              error: getApiErrorMessage(err, TASKLIST_LOAD_MESSAGES.PAGE_FAILED),
            });
          } else {
            commit('SET_TASKLIST_LOAD', {
              status: TASKLIST_LOAD_STATUS.ERROR,
              error: getApiErrorMessage(err, TASKLIST_LOAD_MESSAGES.PAGE_FAILED),
            });
          }
          if (!preserveList) commit('SET_ERROR', err.message || err || 'Unknown error!');
          throw err;
        } finally {
          if (!preserveList) commit('SET_LOADING', false);
          // getchecklist can fail while getdetails is still in flight. Only close
          // the metadata wait once that request has finished, or when the full
          // commit already stored this cycle's metadata.
          if (!detailsSettled && (detailsRequestDone || metadataMatches())) {
            if (metadataMatches()) publishDetails();
            else failDetails(new Error('No analysis data'));
          }
        }
      };

      const inflight = fetchAllTasks();
      const request = {
        checklistId,
        promise: inflight,
        preserveUntilComplete,
        detailsPromise,
      };
      getAllTasksInflight = request;

      try {
        // settle uses this caller's progressive flag. The shared inflight promise
        // still resolves after page 1, so a full-list caller waits and a
        // tasklist caller does not.
        return await settle(await inflight);
      } finally {
        if (getAllTasksInflight === request) {
          getAllTasksInflight = null;
        }
      }
    },

    /**
     * Resolve when this cycle's getdetails metadata is on taskListData.
     * Joins an in-flight non-preserve getAllTasks (that call already posts getdetails).
     * A preserveUntilComplete reload does not post getdetails, so this fetches it once.
     */
    async waitForTasklistDetails({ dispatch, state }, { checklistId }) {
      const matches = () => {
        const id = state.taskListData?.metadata?._id;
        return id != null && String(id) === String(checklistId);
      };
      if (matches()) return state.taskListData;

      const inflight = getAllTasksInflight;
      const sameCycleInflight = inflight && String(inflight.checklistId) === String(checklistId);
      if (sameCycleInflight && inflight.detailsPromise && inflight.preserveUntilComplete !== true) {
        await inflight.detailsPromise;
        if (!matches()) throw new Error('No analysis data');
        return state.taskListData;
      }

      await dispatch('refreshTasklistDetails', { checklistId });
      if (!matches()) throw new Error('No analysis data');
      return state.taskListData;
    },

    /**
     * Phase 2: Fetch checklist separately from getDetails.
     * Calls /v3/financialclose/analysis/getchecklist/:id
     * body {} returns every task (progressive load off). With progressive load on,
     * the first body is offset 0 / limit 100; later pages go through fetchChecklistPageRows.
     * No filters, sort, or include are sent: the server default sort keeps pages stable.
     * @param {Object} payload { checklistId, body?, signal? }
     * @returns {{ checklist, customColumns, customColumnSchema, totalCount }}
     */
    async fetchChecklist({ dispatch, state }, { checklistId, body = {}, signal = null }) {
      // Start getchecklist before formdata resolves so the slow call is not
      // queued behind reference data. Mapping still waits for both. Each
      // promise handles its own rejection so a formdata failure cannot leave
      // the in-flight checklist request as an unhandled rejection.
      const checklistPromise = getAxios()
        .post(CHECKLIST_URL(checklistId), body, signal ? { signal } : undefined)
        .then(
          (response) => ({ ok: true, response }),
          (error) => ({ ok: false, error })
        );
      const formDataPromise = (
        state.formDataV3
          ? Promise.resolve(state.formDataV3)
          : dispatch('fetchFormDataV3')
      ).then(
        (formData) => ({ ok: true, formData }),
        (error) => ({ ok: false, error })
      );

      try {
        const [checklistSettled, formDataSettled] = await Promise.all([
          checklistPromise,
          formDataPromise,
        ]);
        // Formdata failure is reported first, same as awaiting it before the post.
        if (!formDataSettled.ok) throw formDataSettled.error;
        if (!checklistSettled.ok) throw checklistSettled.error;
        const response = checklistSettled.response;
        const formData = formDataSettled.formData;
        const dt = response.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('Failed to fetch checklist');
        const data = dt.data;
        // records contains the enriched checklist array (same shape as Phase 1 checklist[]).
        // Custom fields are flattened and __computed copied onto each task.
        const { rows: tasklistData, customColumns } = mapChecklistRecords(data.records || data.checklist || []);

        const blockedCount = tasklistData.filter((task) => task.blocked === true).length;
        console.log('[tasks]: fetchChecklist blocked', blockedCount, 'of', tasklistData.length);

        //  Extract custom column schema (array of { key, label }) from formData.clientCustomColumns for dynamic rendering in Tasklist and Details pages
        const customColumnSchema = formData?.clientCustomColumns || [];
        addSchemaCustomColumns(customColumns, customColumnSchema);
        return {
          checklist: tasklistData,
          customColumns,
          customColumnSchema,
          totalCount: data.totalCount,
        };
      } catch (err) {
        if (!isRequestCanceled(err)) console.error('[tasks/fetchChecklist]:', err);
        throw err;
      }
    },

    /**
     * Resolve when the current tasklist load for this cycle has every page.
     * Throws when a page failed, so callers that need the full list do not read a partial one.
     */
    async waitForTasklistComplete({ state }, { checklistId }) {
      while (
        tasklistPagesRun
        && String(tasklistPagesRun.token.cycleId) === String(checklistId)
        && isCurrentLoad(state.tasklistLoad, tasklistPagesRun.token)
        && state.tasklistLoad.status === TASKLIST_LOAD_STATUS.LOADING
      ) {
        const run = tasklistPagesRun;
        await run.promise;
        if (tasklistPagesRun === run) break;
      }
      if (
        String(state.tasklistLoad.cycleId) === String(checklistId)
        && state.tasklistLoad.status === TASKLIST_LOAD_STATUS.ERROR
      ) {
        throw new Error(state.tasklistLoad.error || TASKLIST_LOAD_MESSAGES.PAGE_FAILED);
      }
    },

    /**
     * Retry after a failed page. Fetches only the pages not yet received when the
     * loaded rows still belong to the current load; otherwise reloads the list.
     */
    async retryTasklistPages({ commit, dispatch, state }, { checklistId }) {
      const retry = tasklistRetryState;
      if (
        retry
        && String(retry.token.cycleId) === String(checklistId)
        && isCurrentLoad(state.tasklistLoad, retry.token)
        && !retry.token.signal.aborted
      ) {
        tasklistRetryState = null;
        const pages = missingPages(retry.plan, retry.buffer);
        console.log('[tasks]: retryTasklistPages resume', pages.map((entry) => entry.index));
        return startRemainingPages({ commit, state }, { ...retry, pages });
      }
      console.log('[tasks]: retryTasklistPages reload', checklistId);
      return dispatch('getAllTasks', { checklistId, forceRefresh: true, progressive: true });
    },

    /**
     * Stop the tasklist load when leaving Financial Close or switching client:
     * abort page requests and bump the generation so a late response is dropped.
     * A partial list is marked cancelled and reloads on the next visit; a complete
     * list stays cached. Task-detail navigation must not call this — that keeps
     * the in-flight pages and the rows already stored.
     */
    cancelTasklistLoad({ commit, state }, { cycleId = null } = {}) {
      if (cycleId && String(state.tasklistLoad.cycleId) !== String(cycleId)) return;
      abortTasklistRequests();
      getAllTasksInflight = null;
      const { status, generation } = state.tasklistLoad;
      const partial = status === TASKLIST_LOAD_STATUS.LOADING || status === TASKLIST_LOAD_STATUS.ERROR;
      commit('SET_TASKLIST_LOAD', {
        generation: (generation || 0) + 1,
        ...(partial ? { status: TASKLIST_LOAD_STATUS.CANCELLED } : {}),
      });
    },

    /** Client switch: stop the load and drop the previous client's cycle list. */
    resetTasklistForClientSwitch({ commit, dispatch }) {
      dispatch('cancelTasklistLoad');
      commit('CLEAR_TASKLIST');
      commit('SET_TASKLIST_LOAD', {
        cycleId: null,
        status: TASKLIST_LOAD_STATUS.IDLE,
        loaded: 0,
        total: 0,
        error: null,
      });
      commit('SET_CURRENT_TASK', null);
    },

    /**
     * Re-read getdetails for the tasklist (metadata, role summaries, status counts)
     * without touching the rows.
     */
    async refreshTasklistDetails({ commit }, { checklistId }) {
      const response = await getAxios().post(
        `/v3/financialclose/analysis/getdetails/${checklistId}`,
        { page: 'tasklist' },
      );
      const dt = response?.data;
      if (dt?.status !== 'success' || !dt.data) throw new Error(dt?.message || 'No analysis data');
      const data = dt.data;
      commit('SET_TASKLIST_DETAILS', {
        checklistId,
        metadata: data.metadata ? tasklistDetailsMetadata(data.metadata) : null,
        summary: data.summary || null,
      });
      commit('SET_FILTERED_LOCATIONS', { locations: data.filteredLocations || [], cycleId: checklistId });
      commit('SET_STATUS_DETAILS', data.statusDetails || null);
      return data;
    },

    /**
     * Re-read one task with v3 getTask.
     * Writes currentTask and state.tasks[taskId] only.
     * Does not patch taskListData.checklist.
     * @returns {Promise<object>}
     */
    async refreshTaskRow({ dispatch }, { checklistId, taskId }) {
      return dispatch('fetchTask', { checklistId, taskId });
    },

    /**
     * Reload the open task after submit, stage change, refresh, or edit.
     * Awaits v3 getTask and v3 getdetails ({ page: 'tasklist' }).
     * getchecklist and getFormData start immediately and are not awaited.
     * `full` is ignored. getTask returns bot_details for this task.
     * getchecklist still runs in the background and does not block paint.
     * Cycle summaries land on taskListData via refreshTasklistDetails.
     * getTask does not write taskListData.checklist.
     */
    async refreshTaskAfterChange({ dispatch }, { checklistId, taskId }) {
      const wantedId = taskIdString(taskId);
      if (!checklistId || !wantedId) return null;

      dispatch('getAllTasks', {
        checklistId,
        forceRefresh: true,
        preserveUntilComplete: true,
        source: 'task-detail',
      }).catch((err) => {
        console.error('[tasks]: background getchecklist', err?.message || err);
      });
      dispatch('fetchFormDataV3', { forceRefresh: true }).catch((err) => {
        console.error('[tasks]: background fetchFormDataV3', err?.message || err);
      });

      const [task] = await Promise.all([
        dispatch('refreshTaskRow', { checklistId, taskId: wantedId }),
        dispatch('refreshTasklistDetails', { checklistId }),
      ]);
      return task;
    },

    /**
     * Phase 2: Fetch paginated trial balance data for GL picker infinite scroll.
     * Calls POST /v3/financialclose/analysis/getglaccounts/:id
     * Always send the selected task `location` id. When that location's master
     * type is Consolidated, supernova omits the TB location filter and returns
     * cycle TB rows from every entity (NXTGEN-7862). Do not add a second
     * client-side location filter on the picker.
     * @param {Object} payload { projectId, page, limit, search, location, append }
     */
    async fetchTbPage({ commit, state }, { projectId, page = 1, limit = 50, search = null, searchFields = null, location = null, append = false }) {
      // Infinite-scroll appends must not stack; replace/search must always start a new request.
      if (append && (state.tbLoading || state.tbFullyLoaded)) return;

      const { requestToken, signal } = beginTbFetch();
      if (!append) {
        commit('SET_TB_RECORDS', { records: [], append: false });
        commit('SET_TB_FULLY_LOADED', false);
        commit('SET_TB_PAGINATION', { page: 1, limit, totalCount: 0, totalPages: 0 });
      }
      commit('SET_TB_LOADING', true);
      try {
        const body = {
          pagination: { page, limit },
        };
        if (search) body.search = search;
        if (searchFields) body.searchFields = searchFields;
        if (location) body.location = location;

        const response = await getAxios().post(
          `/v3/financialclose/analysis/getglaccounts/${projectId}`,
          body,
          { signal },
        );
        // Ignore stale responses superseded by a newer search/reload.
        if (isStaleTbRequest(requestToken)) return null;

        const dt = response.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('Failed to fetch TB data');
        const data = dt.data;
        const records = data.records || [];
        const totalCount = data.totalCount || 0;
        const totalPages = data.totalPages || 0;

        commit('SET_TB_RECORDS', { records, append });
        commit('SET_TB_PAGINATION', { page, limit, totalCount, totalPages });
        commit('SET_TB_FULLY_LOADED', page >= totalPages || records.length < limit);
        return data;
      } catch (err) {
        if (isTbFetchAborted(err) || isStaleTbRequest(requestToken)) return null;
        console.error('[tasks/fetchTbPage]:', err);
        commit('sbError', err?.message || 'Failed to fetch trial balance data', { root: true });
        return null;
      } finally {
        if (!isStaleTbRequest(requestToken)) {
          commit('SET_TB_LOADING', false);
        }
        if (tbAbortController?.signal === signal) {
          tbAbortController = null;
        }
      }
    },

    /**
     * Load specific trial-balance rows by id without replacing the GL picker page.
     * @param {object} context
     * @param {{ projectId: string, ids: unknown[] }} payload
     * @returns {Promise<object[]>}
     */
    async fetchTbByIds(_, { projectId, ids = [] }) {
      const uniqueIds = [...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || '').trim()).filter(Boolean))];
      if (!projectId || !uniqueIds.length) return [];
      try {
        const response = await getAxios().post(
          `/v3/financialclose/analysis/getglaccounts/${projectId}`,
          { ids: uniqueIds },
        );
        const data = response?.data;
        if (data?.status !== 'success') return [];
        const records = data?.data?.records;
        return Array.isArray(records) ? records : [];
      } catch (err) {
        console.log('[tasks/fetchTbByIds]:', err);
        return [];
      }
    },

    /**
     * Paged cycle tasks for the add/edit dependency picker.
     */
    async fetchTasksForLink(_, { projectId, duedate, excludeTaskId, page = 1, search = '' }) {
      const response = await getAxios().post(
        `/v3/financialclose/analysis/getTasksForLink/${projectId}`,
        {
          duedate,
          excludeTaskId: excludeTaskId || undefined,
          pagination: { page, limit: 20 },
          search: search || undefined,
          searchFields: ['ref', 'description', 'locationName'],
        },
      );
      const body = response?.data;
      if (body?.status !== 'success') {
        throw new Error(body?.message || 'Failed to load tasks');
      }
      const data = body.data || {};
      console.log('[tasks]: fetchTasksForLink', {
        page: data.page || page,
        count: (data.records || []).length,
        totalCount: data.totalCount,
      });
      return {
        records: data.records || [],
        page: data.page || page,
        totalPages: data.totalPages || 1,
        totalCount: data.totalCount || 0,
      };
    },

    /**
     * Link fields from a v3 getTask payload (blocking, blockedBy, relates, canApprove).
     * Pass `task` as the getTask document. When that document has no link lists,
     * `projectId` and `taskId` load them with getTask and do not change currentTask.
     */
    async fetchTaskDependencyDetail(_, payload = {}) {
      let task = payload && typeof payload === 'object' ? payload.task : null;
      if (!taskHasDependencyDetail(task) && payload?.projectId && payload?.taskId) {
        task = await requestGetTask(payload.projectId, payload.taskId);
      }
      if (!taskHasDependencyDetail(task)) {
        console.log('[tasks]: fetchTaskDependencyDetail skipped, no getTask link fields');
        return {};
      }
      console.log('[tasks]: fetchTaskDependencyDetail', {
        blockedBy: (task.blockedBy || []).length,
        blocking: (task.blocking || []).length,
        relates: (task.relates || []).length,
        canApprove: task.canApprove,
      });
      return {
        blocking: task.blocking || [],
        blockedBy: task.blockedBy || [],
        relates: task.relates || [],
        canApprove: task.canApprove,
      };
    },

    /**
     * Merge blocking, blockedBy, relates, and canApprove from a v3 getTask payload
     * onto the cached task. Skips when those lists are already stored, unless forceRefresh.
     * Pass `task` as the getTask document. When link fields are missing and no getTask
     * payload was passed, returns the cached task and does not call the network.
     */
    async hydrateTaskDependencyDetail({ commit, dispatch, state, getters }, {
      projectId,
      taskId,
      forceRefresh = false,
      task = null,
    }) {
      if (!projectId || !taskId) return null;
      const existing = getters.getTaskById(taskId) || state.tasks[taskId] || null;
      if (!forceRefresh && taskHasDependencyDetail(existing)) {
        return existing;
      }

      if (!taskHasDependencyDetail(task)) {
        console.log('[tasks]: hydrateTaskDependencyDetail awaiting v3 getTask', {
          projectId,
          taskId,
        });
        return existing;
      }

      const key = `${projectId}:${taskId}`;
      if (!forceRefresh && hydrateDependencyInflight?.key === key) {
        return await hydrateDependencyInflight.promise;
      }

      const run = (async () => {
        const dependencyDetail = await dispatch('fetchTaskDependencyDetail', { task });
        const base = getters.getTaskById(taskId) || state.tasks[taskId] || { _id: taskId };
        const merged = {
          ...base,
          ...dependencyDetail,
          canApprove: typeof dependencyDetail.canApprove === 'boolean'
            ? dependencyDetail.canApprove
            : base.blocked !== true,
        };
        commit('CACHE_TASK', merged);
        commit('UPDATE_TASK', {
          checklistId: projectId,
          taskId,
          updates: {
            blocking: merged.blocking || [],
            blockedBy: merged.blockedBy || [],
            relates: merged.relates || [],
            canApprove: merged.canApprove,
          },
        });
        if (state.currentTask && taskIdString(state.currentTask._id) === taskIdString(taskId)) {
          commit('SET_CURRENT_TASK', {
            ...state.currentTask,
            blocking: merged.blocking || [],
            blockedBy: merged.blockedBy || [],
            relates: merged.relates || [],
            canApprove: merged.canApprove,
          });
        }
        return merged;
      })();

      hydrateDependencyInflight = { key, promise: run };
      try {
        return await run;
      } finally {
        if (hydrateDependencyInflight?.key === key) {
          hydrateDependencyInflight = null;
        }
      }
    },

    /**
     * Full information-request objects for one task. getTask always returns them.
     * The list row only has counts, so task detail reads this payload.
     * Does not patch the tasklist row.
     * @returns {Promise<object[]>}
     */
    async fetchTaskInfoRequests(_, { checklistId, taskId }) {
      const data = await requestGetTask(checklistId, taskId);
      const requests = data.__computed?.infoManagerRequests;
      return Array.isArray(requests) ? requests : [];
    },

    /**
     * Load one task with v3 getTask.
     * Writes currentTask and state.tasks[taskId] only. Does not call getchecklist,
     * cycle getdetails, or v2 per-task getdetails, and does not copy checklist,
     * usersList, locationList, summary, or metadata onto the task.
     * Does not set state.loading — that flag is the list loader, not this screen.
     */
    async fetchTask({ commit, state, getters }, { checklistId, taskId }) {
      const generation = ++openTaskFetchGeneration;
      const wantedId = taskIdString(taskId);
      try {
        const fresh = await requestGetTask(checklistId, wantedId);
        // A later getTask (other task, or a refresh of this one) owns the store.
        if (generation !== openTaskFetchGeneration) {
          console.log('[tasks]: fetchTask ignored stale response', wantedId);
          return null;
        }
        commit('SET_ERROR', null);
        const existing = getters.getTaskById(wantedId)
          || state.tasks[wantedId]
          || (state.currentTask && taskIdString(state.currentTask._id) === wantedId
            ? state.currentTask
            : null)
          || {};
        const task = taskDetailFromGetTask(existing, fresh);
        // Replace the cache first so SET_CURRENT_TASK cannot keep cycle-wide
        // fields that an older copy of this task still had.
        commit('CACHE_TASK', task);
        commit('SET_CURRENT_TASK', task);
        return task;
      } catch (error) {
        if (generation !== openTaskFetchGeneration) {
          console.log('[tasks]: fetchTask ignored stale error', wantedId);
          return null;
        }
        const notFound = error?.response?.status === 404
          || error?.message === TASK_DETAIL_ERRORS.NOT_FOUND;
        const errorMessage = notFound
          ? TASK_DETAIL_ERRORS.NOT_FOUND
          : TASK_DETAIL_ERRORS.FETCH_FAILED;
        commit('SET_ERROR', errorMessage);
        console.error(errorMessage, error);
        throw new Error(errorMessage);
      }
    },
    
    /**
     * Update a task
     */
    async updateTask({ commit }, { checklistId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // No update API, just refresh all tasks
        const allTasks = await this.dispatch('tasks/getAllTasks', { checklistId });
        return allTasks;
      } catch (error) {
        const errorMessage = 'An error occurred while updating task (refreshing all tasks)';
        commit('SET_ERROR', errorMessage);
        if (error && error.message) {
          console.error(errorMessage, error.message);
        } else {
          console.error(errorMessage, error);
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchNotificationEnabled({ commit, rootState }) {
      try {
        const clientId = rootState.client;
        const response = await getAxios().post(`/v2/client/get/${clientId}`);
        if (
          response?.data &&
          response?.data?.status === "success" &&
          response.data.data &&
          response.data.data.length
        ) {
          const enabled = response.data.data[0]?.schedulerEnabled;
          commit('SET_NOTIFICATION_ENABLED', enabled);
        } else {
          commit('SET_NOTIFICATION_ENABLED', false);
        }
      } catch (err) {
        commit('SET_NOTIFICATION_ENABLED', false);
        // Optionally handle error
      }
    },

  /**
   * Get all task overdue email notifications for a project
   * @param {Object} payload - Contains cycleId
   * @returns {Promise} - Promise resolving to the email notifications
   */
    async getAllTaskEmailNotifications({ commit }, { cycleId }) {
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      try {
        const response = await getAxios().post(
          `${API_ENDPOINTS.GET_ALL_SCHEDULER}/${cycleId}`,
          { notificationType: EMAIL_NOTIFICATION.FEATURE_TYPES.TASK_OVERDUE }
        );
        if (response?.data?.status === 'success') {
          const parsed = parseCycleSchedulerResponse(response.data);
          commit('SET_CYCLE_STATUS', parsed.isCycleActive === false);
          commit('SET_EMAIL_NOTIFICATIONS', parsed.notifications);
          return parsed.notifications;
        }
        const errorMsg = response.data?.message || 'Error retrieving notifications';
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return [];
      } catch (err) {
        console.error('[tasks]: getAllTaskEmailNotifications', err);
        const errorMsg = getApiErrorMessage(err, 'Unknown error fetching notifications');
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return [];
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    
  /**
   * Add a new task overdue email notification
   * @param {Object} payload - Contains notification details
   * @returns {Promise} - Promise resolving to the API response
   */
    async addTaskEmailNotification({ commit, state }, { cycleId, notificationData }) {
      // Check if maximum limit reached
      if (state.emailNotifications.length >= EMAIL_NOTIFICATION.MAX_EMAIL_INSTANCES) {
        const errorMsg = `Current cycle already have maximun notifications. Cannot add more`;
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      }
      
      commit('SET_LOADING', true);
      const data = cycleEscalationFeatureDetails(cycleId, notificationData);
      
      try {
        // Check for duplicates
        const isDuplicate = state.emailNotifications.some(
          (item) =>
            item?.meta?.location_key === notificationData.locations &&
            item?.meta?.category === notificationData.category &&
            item?.meta?.sla === notificationData.sla
        );

        if (isDuplicate) {
          const errorMsg = `Notification already exists for this Location, SLA and Category combo`;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }

        const response = await getAxios().post(
          `${API_ENDPOINTS.ADD_TASK_OVERDUE_NOTIFICATION}/${cycleId}`,
          { featureDetails: data }
        );
        
        if (response?.data?.status === "success") {
          commit('sbSuccess', response.data.message || "Email notification created", { root: true });
          return { success: true, data: response.data };
        } else {
          const errorMsg = response.data?.message || "Error creating notification";
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error creating notification";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch distinct non-AR category/subcategory values from master tasks
     * of the given cycle_type and merge with COA base lists.
     * @param {Object} payload - { cycleType, coaCategoryAcc, coaSubcategoryAcc }
     * @returns {Promise<{ categoryAcc: string[], subcategoryAcc: string[] }>}
     */
    async fetchMasterCategories({ commit }, { cycleType, level = 'master', coaCategoryAcc = [], coaSubcategoryAcc = [] }) {
      try {
        const response = await getAxios().post('/v2/financialclose/analysis/uniquecategories', {
          cycle_type: cycleType.toUpperCase(),
          level,
        });
        let categoryAcc = [...coaCategoryAcc];
        let subcategoryAcc = [...coaSubcategoryAcc];
        if (response?.data?.status === 'success' && response.data.data) {
          const { categories = [], subcategories = [] } = response.data.data;
          const catSet = new Set(categoryAcc);
          const subcatSet = new Set(subcategoryAcc);
          categories.forEach(c => { if (c) catSet.add(c); });
          subcategories.forEach(s => { if (s) subcatSet.add(s); });
          categoryAcc = [...catSet];
          subcategoryAcc = [...subcatSet];
        }
        commit('SET_MASTER_CATEGORIES', { categoryAcc, subcategoryAcc });
        return { categoryAcc, subcategoryAcc };
      } catch (err) {
        console.error('[tasks/fetchMasterCategories] error:', err);
        commit('sbError', err.message || 'Failed to fetch master categories', { root: true });
        return { categoryAcc: coaCategoryAcc, subcategoryAcc: coaSubcategoryAcc };
      }
    },

    async getAllUsersWithStatus({ commit },{isActiveUsersRequired =true}) {
      try {
        const response = await getAxios().post("/v2/client/getAllUsersForEntity",{usersStatus:isActiveUsersRequired});
        if (response?.data?.status !== "success") {
          const errorMsg = response.data?.message || MESSAGES.FAILED_TO_GET_USERS;
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
        }
        const mutationType = isActiveUsersRequired ? 'SET_ACTIVE_USERS' : 'SET_INACTIVE_USERS';
        if (!Array.isArray(response.data.data) || response.data.data.length === 0) {
          commit(mutationType, []);
        }
        commit(mutationType, response.data.data || []);
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error retrieving users";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
      }
    },

  /**
   * Update an existing task overdue email notification
   * @param {Object} payload - Contains notification details with _id
   * @returns {Promise} - Promise resolving to the API response
   */
    async updateTaskEmailNotification({ commit }, { cycleId, notificationData }) {
      if (!notificationData._id) {
        const errorMsg = "Notification ID is required for update";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      
      commit('SET_LOADING', true);
      const data = {
        ...cycleEscalationFeatureDetails(cycleId, notificationData),
        _id: notificationData._id,
      };
      
      try {
        const response = await getAxios().post(
          `${API_ENDPOINTS.EDIT_TASK_OVERDUE_NOTIFICATION}/${cycleId}`,
          { featureDetails: data }
        );
        
        if (response?.data?.status === "success") {
          commit('sbSuccess', response.data.message || "Email notification updated", { root: true });
          return { success: true, data: response.data };
        } else {
          const errorMsg = response.data?.message || "Error updating notification";
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error updating notification";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
  /**
   * Enable or disable a task overdue email notification
   * @param {Object} payload - Contains notification item and enable/disable action
   * @returns {Promise} - Promise resolving to the API response
   */
    async toggleTaskEmailNotificationStatus({ commit, state }, { cycleId, notification, enable }) {
      if (!notification?._id) {
        const errorMsg = "Notification ID is required for status change";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      const currentItem = state.emailNotifications.find(
        item => item?._id.toString() === notification?._id.toString()
      );
      if (!currentItem) {
        const errorMsg = "Notification not found";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      
      const data = {
        feature: notification.feature,
        flag: enable,
        type: EMAIL_NOTIFICATION.TYPE,
        _id: notification._id,
        subject: notification.subject,
        mailBody: notification.mailBody,
        cycle_id: notification.cycle_id,
        schedulerTiming: notification.schedulerTiming,
        isEnableDisable: true, // Flag to indicate this is an enable/disable action
        sla: currentItem?.sla ?? notification?.sla ?? currentItem.meta?.sla ?? notification.meta?.sla ?? '',
        priority: currentItem?.priority ?? notification?.priority ?? currentItem.meta?.priority ?? notification.meta?.priority ?? 'High',
        meta: {
          location_key: notification.meta?.location_key,
          toUsers: notification.meta?.toUsers,
          start: notification.meta?.start ?? notification.meta?.startDate,
          end: notification.meta?.end ?? notification.meta?.endDate,
          startDate: notification.meta?.start ?? notification.meta?.startDate,
          endDate: notification.meta?.end ?? notification.meta?.endDate,
          sla: currentItem?.sla ?? notification?.sla ?? currentItem.meta?.sla ?? notification.meta?.sla ?? '',
          category: currentItem.meta?.category ?? "",
          priority: currentItem?.priority ?? notification?.priority ?? currentItem.meta?.priority ?? notification.meta?.priority ?? 'High',
        }
      };
      
      try {
        const response = await getAxios().post(
          `${API_ENDPOINTS.EDIT_TASK_OVERDUE_NOTIFICATION}/${cycleId}`,
          { featureDetails: data }
        );
        if (response?.data?.status === 'success') {
          const successMsg = enable ? 'Notification Enabled' : 'Notification Disabled';
          commit('sbSuccess', successMsg, { root: true });
          return { success: true, data: response.data };
        }
        const errorMsg = response.data?.message || 'Error updating notification status';
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } catch (err) {
        console.error('[tasks]: toggleTaskEmailNotificationStatus', err);
        const errorMsg = getApiErrorMessage(err, 'Unknown error updating notification status');
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    async deleteTaskEmailNotification({ commit, state }, { cycleId, id }) {
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      try {
        await getAxios().post(`${API_ENDPOINTS.DELETE_CYCLE_EMAIL_NOTIFICATION}/${cycleId}/${id}`, {});
        commit('SET_EMAIL_NOTIFICATIONS', state.emailNotifications.filter((item) => String(item._id) !== String(id)));
        commit('sbSuccess', EMAIL_NOTIFICATION.MESSAGES.SUCCESS_DELETE, { root: true });
      } catch (err) {
        console.error('[tasks]: deleteTaskEmailNotification', err);
        const errorMsg = getApiErrorMessage(err, EMAIL_NOTIFICATION.MESSAGES.FAILED_DELETE);
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        throw err;
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    async triggerTaggedNotification({ commit }, { taggedUsers, msg, task }) {
      try {
        const payload = {
          data: {
            commenter: this.state.user.name || this.state.user.email,
            commentText: msg,
            taskUrl: baseurl + `/financialclose/project/checklist/${task.project}/task/${task._id}?cid=${this.state.client}`,
            taskReference: task.ref || '',
            taskLocation: task.locationName || '',
            taggedUsers,
          },
        };
        await getAxios().post('/v2/financialclose/analysis/triggerTaggedNotification', payload, {
          headers: { 'Content-Type': 'application/json' },
        });
        commit('sbSuccess', 'Tagged notification sent!', { root: true });
        return { success: true };
      } catch (err) {
        commit('sbError', err.message || 'Failed to send tagged notification', { root: true });
        return { success: false, message: err.message };
      }
    },
    /**
     * Bulk update enforceThreshold for a set of tasks.
     * @param {Object} payload { cycleId, taskIds, enforceThreshold, client }
     * @returns {{ success: boolean, message?: string }}
     */
    async bulkUpdateEnforceThreshold({ commit }, { cycleId, taskIds, enforceThreshold, client }) {
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/bulkUpdateEnforceThreshold/${cycleId}`,
          {
            data: [{ enforceThreshold: !!enforceThreshold, task_ids: taskIds }],
            client,
          }
        );
        if (response?.data?.status === 'success') {
          commit('sbSuccess', 'Threshold enforcement updated successfully', { root: true });
          return { success: true };
        } else {
          const msg = response?.data?.message || 'Failed to update threshold enforcement';
          commit('sbError', msg, { root: true });
          return { success: false, message: msg };
        }
      } catch (err) {
        console.error('[StoreAction]: bulkUpdateEnforceThreshold error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to update threshold enforcement', { root: true });
        return { success: false, message: err?.message };
      }
    },
    saveHorizonViewState({ commit }, { projectId, partial }) {
      commit('SET_HORIZON_VIEW_STATE', { projectId, partial });
    },
    saveListViewState({ commit }, { projectId, partial }) {
      commit('SET_LIST_VIEW_STATE', { projectId, partial });
    },
    /**
     * Link bot(s) to task(s) for a cycle.
     * @param {Object} payload { checklistId, links: [{ botid, task_id, bot_result_tags?, tmaid? }] }
     */
    async linkBotTasks({ commit }, { checklistId, links }) {
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/linkbot/${checklistId}`,
          { data: links }
        );
        if (response?.data?.status === 'success') {
          return { success: true, data: response.data.data };
        }
        const msg = response?.data?.message || 'Failed to link bot';
        commit('sbError', msg, { root: true });
        return { success: false, message: msg };
      } catch (err) {
        console.error('[StoreAction]: linkBotTasks error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to link bot', { root: true });
        return { success: false, message: err?.message };
      }
    },
    /**
     * Unlink bot from task(s) for a cycle.
     * @param {Object} payload { checklistId, taskIds: string[] }
     */
    async unlinkBotTasks({ commit }, { checklistId, taskIds }) {
      try {
        const payload = (taskIds || []).map((taskId) => ({ task_id: taskId }));
        const response = await getAxios().post(
          `/v2/financialclose/analysis/unlinkbot/${checklistId}`,
          { data: payload }
        );
        if (response?.data?.status === 'success') {
          return { success: true };
        }
        const msg = response?.data?.message || 'Failed to unlink bot';
        commit('sbError', msg, { root: true });
        return { success: false, message: msg };
      } catch (err) {
        console.error('[StoreAction]: unlinkBotTasks error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to unlink bot', { root: true });
        return { success: false, message: err?.message };
      }
    },
    /** Fetch existing helpdesk tickets for a task. */
    async fetchHelpdeskExistingTickets({ commit }, { checklistId, taskId }) {
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/getexistingtickets/${checklistId}/c/${taskId}`
        );
        if (response?.data?.status === 'success') {
          return { success: true, tickets: response.data.data || [] };
        }
        const errorMsg = response?.data?.message || 'Failed to fetch tickets';
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } catch (err) {
        console.error('[tasks/fetchHelpdeskExistingTickets]:', err);
        commit('sbError', err?.message || 'Failed to fetch tickets', { root: true });
        return { success: false, message: err?.message };
      }
    },
    /** Submit a new helpdesk functional query for a task. */
    async addHelpdeskQuery({ commit }, { checklistId, taskId, issue, description, modulename }) {
      try {
        const response = await getAxios().post(
          `/v2/financialclose/analysis/addfunctionalquery/${checklistId}/c/${taskId}`,
          { data: { issue, description, modulename } }
        );
        if (response?.data?.status === 'success' && response?.data?.data?.length > 0) {
          return { success: true, ticket: response.data.data[0] };
        }
        const msg = response?.data?.message || 'Failed to submit query';
        commit('sbError', msg, { root: true });
        return { success: false, message: msg };
      } catch (err) {
        console.error('[StoreAction]: addHelpdeskQuery error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to submit query', { root: true });
        return { success: false, message: err?.message };
      }
    },
    /**
     * Bulk update due date for a set of tasks.
     * @param {Object} payload { cycleId, taskIds, duedate, note? }
     * @returns {{ success: boolean, message?: string }}
     */
    async bulkUpdateDueDate({ commit }, { cycleId, taskIds, duedate, note }) {
      try {
        const body = { duedate, _id: taskIds };
        if (note) {
          body.note = note;
        }
        const response = await getAxios().post(
          `/v2/financialclose/analysis/bulkupdateduedate/${cycleId}`,
          { data: body }
        );
        if (response?.data?.status === 'success') {
          commit('sbSuccess', 'Updated Due Date', { root: true });
          return { success: true };
        } else {
          const msg = response?.data?.message || 'Failed to update due date';
          commit('sbError', msg, { root: true });
          return { success: false, message: msg };
        }
      } catch (err) {
        console.error('[StoreAction]: bulkUpdateDueDate error', err);
        commit('sbError', err?.response?.data?.message || err?.message || 'Failed to update due date', { root: true });
        return { success: false, message: err?.message };
      }
    },
  },
  
  getters: {
    getCustomLayout: (state) => (pageName) => (
      pageName ? state.customLayouts[pageName] ?? null : null
    ),
    /** True once fetchCustomLayout cached this page (the cached value may be null: no saved layout). */
    hasCustomLayout: (state) => (pageName) => (
      !!pageName && Object.prototype.hasOwnProperty.call(state.customLayouts, pageName)
    ),
    getStatusDetails: (state) => state.statusDetails,
    getTransactionMatchingList: (state) => state.transactionMatchingList,
    /**
     * Get a task by ID
     */
    getTaskById: (state) => (taskId) => {
      const wantedId = taskIdString(taskId);
      // Prefer currentTask when it matches. This prevents stale cached data from
      // overriding the task detail page state during role switching / refresh flows.
      if (state.currentTask && taskIdString(state.currentTask._id) === wantedId) {
        return state.currentTask;
      }
      if (wantedId && state.tasks[wantedId]) return state.tasks[wantedId];
      if (taskId && state.tasks[taskId]) return state.tasks[taskId];

      // Fallback to taskListData if present (covers cases where getAllTasks refreshed
      // the checklist but the per-task cache hasn't been populated yet).
      const checklist = state.taskListData && Array.isArray(state.taskListData.checklist)
        ? state.taskListData.checklist
        : [];
      return checklist.find((row) => row && taskIdString(row._id) === wantedId) || null;
    },
    
    /**
     * Get all tasks for a checklist
     */
    getTaskList: (state) => (checklistId) => {
      return state.taskLists[checklistId] || [];
    },
    // Get user details with status
    getUserDetails:(state) => state.userDetails || {},
    getActiveUsersList:(state) => state.activeUsers || [],
    getActiveUsersIds:(state) => state.activeUsers.map(user => user?._id || user?.id).filter(Boolean) || [],
    getInactiveUsersList:(state) => state.inactiveUsers || [],
    getMasterCategories: (state) => state.masterCategories || { categoryAcc: [], subcategoryAcc: [] },

    /**
     * Get loading status
     */
    isLoading: (state) => state.loading,
    
    /**
     * Get error status
     */
    getError: (state) => state.error,
    
    /**
     * Get current task
     */
    getCurrentTask: (state) => state.currentTask,
    
    /**
     * Get cached task list data
     */
    getTaskListData: (state) => {
      return state.taskListData;
    },
    getTasklistLoad: (state) => state.tasklistLoad,
    /**
     * Every row of the current cycle is in the store. Select-all, bulk actions,
     * export and column filters wait for this (NXTGEN-8043 Q7).
     */
    isTasklistFullyLoaded: (state) => state.tasklistLoad.status === TASKLIST_LOAD_STATUS.COMPLETE
      || state.tasklistLoad.status === TASKLIST_LOAD_STATUS.IDLE,
    isNotificationEnabled: (state) => state.notificationEnabled,
    /**
     * Get email notifications
     */
    getTaskEmailNotifications: (state) => state.emailNotifications,
    /**
     * Get cycle inactive status
     */
    isCycleInactive: (state) => state.isCycleInActive,

    getUsersList: (state) => state.usersList,

    /**
     * O(1) lookup map for user ID → user object { _id, name, email, isActive }.
     * Built from formDataV3 (v3 consolidated endpoint) or legacy usersList.
     * Components should use this instead of .find() for ID→name resolution.
     */
    getUserMap: (state) => {
      const users = state.formDataV3?.users || state.taskListData?.usersList || [];
      if (!Array.isArray(users)) return {};
      const map = {};
      for (const u of users) {
        if (u && u._id) map[u._id] = u;
      }
      return map;
    },

    /**
     * O(1) lookup map for user email → user _id.
     * Used for bulk upload email→ID resolution.
     */
    getUserEmailMap: (state) => {
      const users = state.formDataV3?.users || state.taskListData?.usersList || [];
      if (!Array.isArray(users)) return {};
      const map = {};
      for (const u of users) {
        if (u && u.email) map[u.email.trim()] = u._id;
      }
      return map;
    },

    /**
     * O(1) lookup map for location ID → location object { _id, name, ... }.
     * Components should use this instead of .find() for location resolution.
     */
    getLocationMap: (state) => {
      const locations = state.formDataV3?.locations || state.taskListData?.locationList || [];
      if (!Array.isArray(locations)) return {};
      const map = {};
      for (const loc of locations) {
        if (loc && loc._id) map[loc._id] = loc;
      }
      return map;
    },
    
    /**
     * Get location list from cached task data
     */
    getLocationList: (state) => state.locationList || [],
    
    /**
     * Get enabled (actor-scoped) locations for dropdowns.
     * Populated from getFormDataV3.actorLocations via SET_ENABLED_LOCATIONS.
     */
    getEnabledLocations: (state) => state.enabledLocations || [],

    /**
     * Get filtered locations (cycle ∩ actor locations)
     */
    getFilteredLocations: (state) => state.filteredLocations || [],

    /**
     * Cycle the filtered location list was computed for.
     * Null until getAllTasks or ensureFilteredLocations has run.
     * An empty list with a matching cycle id is a real intersection, not "not loaded".
     */
    getFilteredLocationsCycleId: (state) => state.filteredLocationsCycleId,

    /**
     * Get v3 formdata (client-level reference data: users, locations, categories)
     */
    getFormDataV3: (state) => state.formDataV3,

    getHorizonViewState: (state) => (projectId) => state.horizonViewState[projectId] || null,

    getListViewState: (state) => (projectId) => state.listViewState[projectId] || null,

    getTasklistView: (state) => (checklistId) => state.tasklistViewByChecklist[checklistId] || null,
    
    /**
     * Get cycle metadata from cached task data
     * Used to avoid redundant dashboard API calls
     */
    getCycleMetadata: (state) => state.taskListData?.metadata || null,
    
    /**
     * Get role-specific summaries
     */
    getManagerSummary: (state) => state.managersummary || {},
    getPreparerSummary: (state) => state.preparersummary || {},
    getApproverSummary: (state) => state.approversummary || {},

    // TB pagination getters (Phase 2)
    getTbRecords: (state) => state.tbRecords,
    getTbPagination: (state) => state.tbPagination,
    isTbLoading: (state) => state.tbLoading,
    isTbFullyLoaded: (state) => state.tbFullyLoaded,
  },
};
